import {AlpineShelter,alpineEnabled,snowPotential} from "./alpineHabitat.js";
/**
 * Procedural universe generation.
 *
 * The entire universe is a pure function of string seeds: a galaxy coordinate
 * names a star system, a star system deterministically yields its star and
 * planets, and a planet's terrain height is a pure function of its seed and a
 * unit direction. No world data is stored or transmitted — every client and
 * the server regenerate identical worlds locally.
 *
 * Terrain v2 — rich worlds out of pure functions:
 *  - ARCHETYPES (terran / ocean / desert / ice / volcanic / barren) set the
 *    climate, sea, palette and terrain character of each planet.
 *  - DOMAIN WARPING bends the sample space with low-frequency noise, so
 *    coastlines and ranges flow instead of reading as round noise blobs.
 *  - A CONTINENTALNESS SPLINE remaps one master field into ocean floor →
 *    shelf → flat coastal plains → rising inland → highlands.
 *  - MOUNTAINS are ridged multifractals confined by a broad placement mask
 *    (and skipped entirely where the mask is empty — plains stay cheap).
 *  - RIVERS trace the native terrain with bounded channel incision; LAKES are
 *    basin depressions in the plains. The sea sphere fills their submerged
 *    outlets, while inland rivers have their own descending water surfaces.
 *  - CLIMATE (temperature from latitude + altitude lapse + noise, moisture
 *    from noise + coastal bonus) drives Whittaker-style biome coloring and
 *    scatter on the client — continuous everywhere, no hard borders.
 */
import { rngFromString, range, rangeInt, hashSeed, type Rng } from "./rng.js";
import { generatedPlanetName, generatedSystemName } from "./worldgen/names.js";
import * as pmath from "./pmath.js";
import { SimplexNoise3D } from "./noise.js";
import { LocalLandforms } from "./landforms.js";
import { MountainLandforms } from './mountainLandforms.js';
import { RegionalLandforms, regionalSample, type RegionalLandform, type RegionalSample } from './regionalLandforms.js';
import {DuneTerrain,duneSample,type DuneDef,type DuneNativeSample} from './dunes.js';
import {ButteTerrain,butteSample,type ButteDef} from './buttes.js';
import {CanyonTerrain,canyonSample,type CanyonDef,type CanyonNative} from './canyons.js';
import {InlandWetlandTerrain,inlandWetlandSample,type InlandWetlandDef,type InlandWetlandNative} from './inlandWetlands.js';
import { CoastalLandforms, coastalSample, type CoastalLandform, type CoastalSample } from './coastalLandforms.js';
import { EstuaryField, estuarySample, type EstuaryDef, type EstuarySample } from './estuaries.js';
import { GlacialTerrain, type GlacierDef, type GlacialSample } from './glaciers.js';
import { VolcanicTerrain, type VolcanoDef, type VolcanicSample } from './volcanoes.js';
import { buildRiverGraph, carveRiverHeight, riverBankMoisture, type RiverGraph, type RiverHit } from "./rivers.js";
import {riverBankSample,sampleRiverBank} from './riverBanks.js';
import {sampleRiverEscarpments,escarpmentSample,ESCARPMENT_MAX_CUT_M} from './riverEscarpments.js';
import {riverWaterClaim} from './riverPools.js';
import { townFieldFor, type TownField } from "./towns.js";
// Type-only: carveindex imports PlanetSampler type-only in return, so neither
// side pulls the other at runtime — no import cycle. The stage below consumes
// the index purely through its structural CarveHit output.
import type { SettlementIndex, CarveHit } from "./worldgen/carveindex.js";
import type { SeaSurface } from './surface.js';

/**
 * Global world-size multiplier. Bigger worlds, same human-scale detail:
 * everything that defines a body's SIZE or a system's DISTANCES (planet/moon/
 * star radii, orbital radii + spacing, belt extents) is multiplied by this, so
 * the macro structure looks identical from orbit but a planet's surface holds
 * far more ground to explore.
 *
 * What is DELIBERATELY NOT scaled by it:
 *  - amplitudeRel / terrain frequencies — ratios of the radius, so terrain
 *    relief and continent layout already scale with the body for free.
 *  - orbital/spin PERIODS — angular motion stays the same (the Kepler distance
 *    reference below is scaled in lockstep so periods don't balloon).
 *  - human-scale detail (flora, rivers, rocks, ruins, stations) — kept at real
 *    size on purpose; tuned separately so the bigger worlds feel richer, not
 *    just emptier.
 */
export const WORLD_SCALE = 5;

/** Distance reference for the Kepler period law, scaled so angular periods are
 *  invariant under WORLD_SCALE (a planet at the same orbit index keeps its
 *  old sky-motion speed instead of crawling). */
const KEPLER_REF = 70000 * WORLD_SCALE;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface StarDef {
  /** Generation seed — surface character derives from it, like planets. */
  seed: string;
  /** Surface color as 0xRRGGBB. */
  color: number;
  radius: number;
  /** Light intensity multiplier. */
  intensity: number;
}

export type PlanetArchetype =
  | "terran"
  | "ocean"
  | "desert"
  | "ice"
  | "volcanic"
  | "barren";

export interface TerrainDef {
  /** Max displacement as a fraction of planet radius. */
  amplitudeRel: number;
  continentFreq: number;
  mountainFreq: number;
  detailFreq: number;
  /** How mountainous (0 = rolling hills, 1 = sharp ridges). */
  ridginess: number;
  /** Domain warp strength (fraction of the unit-sphere coordinate). */
  warpAmp: number;
  hillFreq: number;
  /** 0 = no rivers; 1 = dense channel network. */
  riverDensity: number;
  riverFreq: number;
  /** 0..1 mesa/terrace tendency (deserts, volcanic flows). */
  plateau: number;
}

export interface OrbitDef {
  /** Orbital period around the parent (seconds, wall clock). */
  periodS: number;
  /** Unit normal of the orbit plane. */
  normal: [number, number, number];
}

export interface SpinDef {
  /** Rotation axis (unit, system space) — near the orbital pole, tilted. */
  axis: [number, number, number];
  /** Day length in real seconds. */
  periodS: number;
  /** Rotation phase at t=0 (radians). */
  phase: number;
}

export interface ClimateDef {
  /** Base offsets in [-1, 1] shifting the whole planet hot/cold, wet/dry. */
  temp: number;
  moist: number;
  /** Equator-to-pole cooling strength. */
  latitude: number;
  /** Altitude (in amplitude units) where snow starts; >2 = never. */
  snowline: number;
}

/**
 * Whittaker-style palette: a 4 (temperature rows, cold→hot) × 3 (moisture
 * columns, dry→wet) ground-color table, blended bilinearly, plus the
 * special surfaces.
 */
export interface PaletteDef {
  table: [number, number, number][];
  cliff: [number, number, number];
  sand: [number, number, number];
  snow: [number, number, number];
  /** Painted water for carved channels above sea level. */
  water: [number, number, number];
}

export interface PlanetDef {
  id: string;
  seed: string;
  name: string;
  radius: number;
  /** Position in system space (static — planets don't orbit in realtime). */
  position: [number, number, number];
  archetype: PlanetArchetype;
  terrain: TerrainDef;
  climate: ClimateDef;
  /**
   * Day/night spin. The GEOMETRY never rotates (terrain must stay a pure
   * function of seed + direction for collision/server/multiplayer) — the
   * renderer rotates the SUN around this axis instead, which is the same
   * physics seen from the planet's surface.
   */
  spin: SpinDef;
  /**
   * Orbit around the star (planets) or the parent planet (moons).
   * `position` is the body's location at t = 0; the live center is
   * position rotated about the orbit normal by 2π·t/periodS. Pure wall
   * clock — every client and the server agree without any sync.
   */
  orbit: OrbitDef;
  palette: PaletteDef;
  /** Sea radius (absolute, from planet center), or null for dry worlds. */
  seaRadius: number | null;
  /** Explicit basin mechanics. Absent means legacy water, including ice/volcanic
   * archetypes. Generation does not opt in until feature/migration rules exist. */
  seaSurface?: SeaSurface;
  seaColor: [number, number, number];
  atmosphere: { color: [number, number, number]; height: number } | null;
  /** Scatter object tint (rocks / flora). */
  scatterColor: [number, number, number];
  hasFlora: boolean;
  /** Set on moons: the planet this body orbits. */
  parentId?: string;
  /** C4: true on the guaranteed-habitable planet 0 of a home:<uid> system —
   *  tierRegistry guarantees its town-tier capital; UI shows HOME affordances. */
  home?: boolean;
  /** Planetary ring system (giants mostly). Radii relative to planet radius. */
  ring?: { inner: number; outer: number; color: [number, number, number]; opacity: number };
}

/** An asteroid belt: a seeded annulus of drifting rock between orbits. */
export interface BeltDef {
  seed: string;
  /** Annulus center radius and half-width (system units). */
  radius: number;
  width: number;
  /** Vertical half-thickness. */
  thickness: number;
  /** Unit normal of the belt plane. */
  normal: [number, number, number];
  count: number;
}

export interface SystemDef {
  /** Galaxy coordinate id, e.g. "0:0:0". */
  id: string;
  seed: string;
  name: string;
  star: StarDef;
  planets: PlanetDef[];
  belts: BeltDef[];
  /** Galactic region character at this lattice point (see regionAt). */
  region: RegionDef;
}

/**
 * Galactic structure: two continuous low-frequency fields over the integer
 * lattice. DENSITY shapes how crowded systems are (rich cores, sparse
 * frontier); TEMP tilts archetypes (ember regions run volcanic/desert,
 * cold reaches run ice). Continuous across neighbors — flying spinward you
 * feel the galaxy change, not flicker.
 */
export interface RegionDef {
  /** 0 sparse frontier → 1 dense core. */
  density: number;
  /** -1 cold reach → +1 ember region. */
  temp: number;
}

const REGION_NOISE = new SimplexNoise3D(hashSeed("hyperlight:galaxy:density"));
const REGION_TEMP = new SimplexNoise3D(hashSeed("hyperlight:galaxy:temp"));

export function regionAt(gx: number, gy: number, gz: number): RegionDef {
  const f = 0.09;
  const density = Math.min(1, Math.max(0, 0.5 + REGION_NOISE.fbm(gx * f, gy * f, gz * f, 3) * 0.9));
  const temp = Math.max(-1, Math.min(1, REGION_TEMP.fbm(gx * f * 1.4, gy * f * 1.4, gz * f * 1.4, 3) * 1.6));
  return { density, temp };
}

// ---------------------------------------------------------------------------
// Name generation — worldgen/names.ts speaks here: a region-keyed Language
// plus a per-system family trait (see celestial naming there). Names are
// hash-keyed, never drawn from the generation rng stream.
// ---------------------------------------------------------------------------

/** Systems within one 8³ lattice block share a naming Language, so names
 *  cluster into audible "constellations". Sealed ids carry lattice coords
 *  too; home and cache pockets have none and speak their own seed's tongue. */
function namingRegionKey(id: string): string | null {
  const m = /^(?:sealed:)?(-?\d+):(-?\d+):(-?\d+)$/.exec(id);
  if (!m) return null;
  return `celestial:${Math.floor(+m[1] / 8)}:${Math.floor(+m[2] / 8)}:${Math.floor(+m[3] / 8)}`;
}

/** Survey letters for unnamed rock, exoplanet-style — the star is implicitly "a". */
const PLANET_LETTERS = "bcdefghijklmnop";

// ---------------------------------------------------------------------------
// System generation
// ---------------------------------------------------------------------------

const STAR_CLASSES: { color: number; weight: number }[] = [
  { color: 0xfff4e0, weight: 4 }, // yellow-white
  { color: 0xffd9a0, weight: 3 }, // orange
  { color: 0xcfe0ff, weight: 2 }, // blue-white
  { color: 0xffb3b3, weight: 1 }, // red
];

function hsvToRgb(h: number, s: number, v: number): [number, number, number] {
  const i = Math.floor(((h % 1) + 1) * 6) % 6;
  const f = ((h % 1) + 1) * 6 - Math.floor(((h % 1) + 1) * 6);
  const p = v * (1 - s);
  const q = v * (1 - f * s);
  const t = v * (1 - (1 - f) * s);
  switch (i) {
    case 0: return [v, t, p];
    case 1: return [q, v, p];
    case 2: return [p, v, t];
    case 3: return [p, q, v];
    case 4: return [t, p, v];
    default: return [v, p, q];
  }
}

const ARCHETYPES: { kind: PlanetArchetype; weight: number }[] = [
  { kind: "terran", weight: 28 },
  { kind: "ocean", weight: 12 },
  { kind: "desert", weight: 16 },
  { kind: "ice", weight: 13 },
  { kind: "volcanic", weight: 12 },
  { kind: "barren", weight: 19 },
];

/**
 * Whittaker ground table builder. Rows cold→hot, columns dry→wet. Each
 * archetype commits to a family of hues; `hue` gives the planet identity
 * drift so two terran worlds never read identically.
 */
function buildPalette(kind: PlanetArchetype, hue: number, rng: Rng): PaletteDef {
  const j = (a: number, b: number): number => range(rng, a, b);
  const drift = (h: number): number => h + (hue - 0.5) * 0.1;
  let table: [number, number, number][];
  let cliff: [number, number, number];
  let sand: [number, number, number];
  let water: [number, number, number] = hsvToRgb(drift(0.55), j(0.5, 0.75), j(0.4, 0.6));
  const snow: [number, number, number] = [j(0.86, 0.95), j(0.88, 0.96), j(0.94, 1)];

  switch (kind) {
    case "terran":
    case "ocean": {
      // Keep seed variation within living greens; the old wide hue drift
      // could push whole temperate worlds into yellow-brown or blue-green.
      const green = 0.29 + j(-0.02, 0.035) + (hue - 0.5) * 0.035;
      const warmDry = drift(0.1 + j(-0.02, 0.03));
      table = [
        // cold: tundra → taiga → boreal
        hsvToRgb(warmDry, j(0.18, 0.3), 0.5), hsvToRgb(green, 0.38, 0.34), hsvToRgb(green + 0.04, 0.45, 0.3),
        // cool: steppe → grassland → forest
        hsvToRgb(warmDry, 0.40, 0.55), hsvToRgb(green, 0.64, 0.48), hsvToRgb(green, 0.66, 0.40),
        // warm: savanna → meadow → deep forest
        hsvToRgb(warmDry, 0.52, 0.62), hsvToRgb(green - 0.012, 0.66, 0.54), hsvToRgb(green + 0.02, 0.74, 0.36),
        // hot: desert → shrubland → jungle
        hsvToRgb(warmDry, 0.45, 0.78), hsvToRgb(green - 0.06, 0.5, 0.45), hsvToRgb(green, 0.78, 0.26),
      ];
      cliff = hsvToRgb(warmDry, j(0.12, 0.25), j(0.3, 0.42));
      sand = hsvToRgb(warmDry + 0.02, j(0.3, 0.45), j(0.7, 0.82));
      break;
    }
    case "desert": {
      // Golden-yellow sand (hue ~0.11 ≈ 40°, richer saturation) instead of the old
      // pale, near-orange tan that read as washed-out/white in bright sun.
      const base = drift(0.11 + j(-0.01, 0.03));
      table = [
        hsvToRgb(base, 0.4, 0.55), hsvToRgb(base, 0.45, 0.5), hsvToRgb(base + 0.04, 0.4, 0.45),
        hsvToRgb(base, 0.55, 0.66), hsvToRgb(base, 0.6, 0.58), hsvToRgb(base + 0.06, 0.5, 0.5),
        hsvToRgb(base, 0.68, 0.74), hsvToRgb(base + 0.02, 0.68, 0.64), hsvToRgb(0.3 + (hue - 0.5) * 0.08, 0.35, 0.45),
        hsvToRgb(base, 0.72, 0.82), hsvToRgb(base + 0.03, 0.72, 0.72), hsvToRgb(base + 0.05, 0.6, 0.56),
      ];
      cliff = hsvToRgb(base - 0.03, 0.55, 0.36);
      sand = hsvToRgb(base, 0.65, 0.85);
      break;
    }
    case "ice": {
      const blue = drift(0.55 + j(-0.04, 0.04));
      table = [
        // cold: pack ice → blue ice → glacial sheet
        [...snow], [0.8, 0.86, 0.92], hsvToRgb(blue, 0.2, 0.85),
        // cool: firn → pale ice → crevasse blue
        [0.78, 0.8, 0.84], hsvToRgb(blue, 0.16, 0.74), hsvToRgb(blue, 0.25, 0.68),
        // warm: exposed grey rock → thawing ice → meltwater stain
        [0.66, 0.68, 0.73], hsvToRgb(blue, 0.1, 0.62), hsvToRgb(blue + 0.03, 0.22, 0.58),
        // hot (equator): bare rock → old ice → deep melt
        [0.6, 0.64, 0.7], hsvToRgb(blue, 0.18, 0.55), hsvToRgb(blue + 0.05, 0.3, 0.5),
      ];
      cliff = hsvToRgb(blue, 0.12, 0.4);
      sand = [0.75, 0.8, 0.86];
      water = hsvToRgb(blue, 0.4, 0.7);
      break;
    }
    case "volcanic": {
      const ember = drift(0.03 + j(-0.01, 0.02));
      table = [
        [0.16, 0.14, 0.14], [0.2, 0.17, 0.16], [0.24, 0.2, 0.18],
        [0.22, 0.18, 0.16], hsvToRgb(ember, 0.5, 0.3), hsvToRgb(ember, 0.4, 0.26),
        [0.26, 0.2, 0.17], hsvToRgb(ember, 0.6, 0.36), hsvToRgb(ember + 0.02, 0.5, 0.3),
        hsvToRgb(ember, 0.65, 0.42), hsvToRgb(ember, 0.75, 0.5), hsvToRgb(ember + 0.03, 0.6, 0.34),
      ];
      cliff = [0.12, 0.11, 0.12];
      sand = [0.3, 0.24, 0.2];
      water = hsvToRgb(0.05, 0.95, 0.95); // lava
      break;
    }
    default: {
      // barren
      const grey = drift(0.08 + j(-0.04, 0.05));
      table = [
        hsvToRgb(grey, 0.08, 0.4), hsvToRgb(grey, 0.1, 0.45), hsvToRgb(grey, 0.12, 0.5),
        hsvToRgb(grey, 0.1, 0.48), hsvToRgb(grey, 0.14, 0.52), hsvToRgb(grey, 0.16, 0.55),
        hsvToRgb(grey, 0.12, 0.55), hsvToRgb(grey, 0.16, 0.58), hsvToRgb(grey, 0.18, 0.6),
        hsvToRgb(grey, 0.15, 0.62), hsvToRgb(grey, 0.18, 0.64), hsvToRgb(grey, 0.2, 0.66),
      ];
      cliff = hsvToRgb(grey, 0.08, 0.3);
      sand = hsvToRgb(grey, 0.12, 0.55);
      water = hsvToRgb(drift(0.55), 0.4, 0.5);
      break;
    }
  }
  return { table, cliff, sand, snow, water };
}

function generatePlanet(
  systemSeed: string,
  index: number,
  orbitRadius: number,
  regionTemp = 0,
  home = false,
  systemName = "",
  regionKey = systemSeed,
): PlanetDef {
  const seed = `${systemSeed}:planet:${index}`;
  const rng = rngFromString(seed);

  const radius = range(rng, 1600, 4200) * WORLD_SCALE;
  const angle = range(rng, 0, Math.PI * 2);
  const inclination = range(rng, -0.12, 0.12);
  const position: [number, number, number] = [
    pmath.cos(angle) * orbitRadius,
    pmath.sin(inclination) * orbitRadius,
    pmath.sin(angle) * orbitRadius,
  ];

  // Archetype roll, tilted by the galactic region: ember regions run
  // volcanic and dry, cold reaches run icy, temperate space stays lush.
  const weights = ARCHETYPES.map((a) => {
    let w = a.weight;
    if (a.kind === "ice") w *= 1 - regionTemp * 0.7;
    if (a.kind === "desert" || a.kind === "volcanic") w *= 1 + regionTemp * 0.7;
    if (a.kind === "terran" || a.kind === "ocean") w *= 1 - Math.abs(regionTemp) * 0.35;
    return Math.max(1, w);
  });
  const totalW = weights.reduce((s, w) => s + w, 0);
  let roll = rng() * totalW;
  let kind: PlanetArchetype = "terran";
  for (let i = 0; i < ARCHETYPES.length; i++) {
    roll -= weights[i];
    if (roll <= 0) {
      kind = ARCHETYPES[i].kind;
      break;
    }
  }
  // C4 home planet: the archetype roll is CONSUMED above (identical rng
  // stream shape either way) but the outcome is forced — the home world is
  // guaranteed terran, and terran's own derivations below then guarantee a
  // sea (seaRadius always set for terran) and flora.
  if (home) kind = "terran";

  const hue = rng();
  const terran = kind === "terran" || kind === "ocean";

  const terrain: TerrainDef = {
    // Base relief is kept GENTLE (near the original) so coasts, plains and
    // highlands stay walkable and flat-reading — the "vaster mountains" come
    // from a separate boost on the ridged mountain term in sample(), NOT from a
    // big global amplitude (which would steepen shorelines + fields too).
    amplitudeRel: range(rng, 0.024, 0.052) * (kind === "volcanic" ? 1.3 : kind === "ocean" ? 0.8 : 1),
    continentFreq: range(rng, 0.7, 1.6),
    mountainFreq: range(rng, 2.6, 5.5),
    detailFreq: range(rng, 130, 280),
    ridginess: kind === "volcanic" ? range(rng, 0.7, 1) : range(rng, 0.25, 0.95),
    warpAmp: range(rng, 0.15, 0.4),
    hillFreq: range(rng, 3.5, 7),
    riverDensity:
      terran ? range(rng, 0.6, 1) : kind === "ice" ? range(rng, 0.2, 0.5) : kind === "desert" ? range(rng, 0, 0.25) : 0,
    riverFreq: range(rng, 2.2, 4),
    plateau: kind === "desert" || kind === "volcanic" ? range(rng, 0.3, 0.7) : range(rng, 0, 0.15),
  };

  const climate: ClimateDef = {
    temp:
      kind === "ice" ? range(rng, -0.95, -0.6)
      : kind === "desert" ? range(rng, 0.35, 0.7)
      : kind === "volcanic" ? range(rng, 0.5, 0.9)
      : range(rng, -0.25, 0.35),
    moist:
      kind === "desert" || kind === "volcanic" || kind === "barren"
        ? range(rng, -0.8, -0.4)
        : kind === "ocean" ? range(rng, 0.3, 0.7)
        : range(rng, -0.35, 0.3),
    latitude: range(rng, 0.5, 1),
    snowline:
      kind === "ice" ? 0.05
      : terran ? range(rng, 0.55, 0.95)
      : kind === "desert" ? 1.6
      : 3, // volcanic/barren: never
  };

  const amplitude = radius * terrain.amplitudeRel;
  let seaRadius: number | null = null;
  if (kind === "ocean") seaRadius = radius + amplitude * range(rng, 0.3, 0.5);
  else if (terran) seaRadius = radius + amplitude * range(rng, -0.16, 0.0);
  else if (kind === "ice") seaRadius = radius + amplitude * range(rng, -0.12, 0.02);
  else if (kind === "volcanic" && rng() > 0.5) seaRadius = radius + amplitude * range(rng, -0.25, -0.1);
  else if (kind === "desert" && rng() > 0.85) seaRadius = radius + amplitude * range(rng, -0.3, -0.2);

  const palette = buildPalette(kind, hue, rng);

  // Day length 8–30 real minutes; axis tilted up to ~30° off the pole.
  const tiltA = range(rng, 0, Math.PI * 2);
  const tilt = range(rng, 0, 0.5);
  const spinAxis: [number, number, number] = [
    pmath.sin(tilt) * pmath.cos(tiltA),
    pmath.cos(tilt),
    pmath.sin(tilt) * pmath.sin(tiltA),
  ];
  // Day length normalized around 30 real minutes, per-planet variance.
  const spin: SpinDef = {
    axis: spinAxis,
    periodS: range(rng, 2900, 4700), // ~one-hour days, per-planet variance
    phase: range(rng, 0, Math.PI * 2),
  };
  // Kepler-flavored: outer orbits run slower. First planet ≈ 2.5 h.
  const orbit: OrbitDef = {
    periodS: 27000 * pmath.pow(orbitRadius / KEPLER_REF, 1.5), // stately — a sky feature, not a treadmill
    normal: (() => {
      const tA = range(rng, 0, Math.PI * 2);
      const tI = range(rng, 0, 0.1); // orbits hug the ecliptic
      return [pmath.sin(tI) * pmath.cos(tA), pmath.cos(tI), pmath.sin(tI) * pmath.sin(tA)] as [
        number,
        number,
        number,
      ];
    })(),
  };

  const seaColor: [number, number, number] =
    kind === "volcanic"
      ? [1, 0.32, 0.08] // lava sea
      : kind === "ice"
        ? hsvToRgb(0.55, 0.25, 0.85) // frozen pale
        : hsvToRgb(0.52 + (hue - 0.5) * 0.12, range(rng, 0.55, 0.85), range(rng, 0.35, 0.55));

  // Home worlds always breathe — the 12% airless roll is consumed, overridden.
  const hasAtmosphere = (kind === "barren" ? rng() > 0.75 : rng() > 0.12) || home;
  const atmoHue =
    kind === "desert" ? 0.08 : kind === "volcanic" ? 0.04 : kind === "ice" ? 0.56 : terran ? 0.58 : 0.5 + hue * 0.1;
  const atmosphere = hasAtmosphere
    ? {
        color: hsvToRgb(atmoHue, range(rng, 0.35, 0.65), range(rng, 0.7, 0.95)),
        height: radius * range(rng, 0.04, 0.08),
      }
    : null;

  // Rings favor the big worlds: a banded halo of ice and dust.
  const ring =
    rng() < (radius > 3000 ? 0.3 : 0.1)
      ? {
          inner: range(rng, 1.5, 1.9),
          outer: range(rng, 2.2, 3),
          color: hsvToRgb(0.07 + hue * 0.5, range(rng, 0.08, 0.3), range(rng, 0.55, 0.8)) as [number, number, number],
          opacity: range(rng, 0.35, 0.7),
        }
      : undefined;

  // Living worlds earn a native name in the region's tongue; barren rock
  // stays a survey designation until someone has a reason to say otherwise.
  const name = kind === "barren" && !home
    ? `${systemName} ${PLANET_LETTERS[index] ?? index + 1}`
    : generatedPlanetName(systemSeed, index, regionKey);

  return {
    id: seed,
    seed,
    name,
    radius,
    position,
    archetype: kind,
    terrain,
    climate,
    spin,
    orbit,
    palette,
    seaRadius,
    seaColor,
    atmosphere,
    scatterColor:
      kind === "desert"
        ? hsvToRgb(0.22, range(rng, 0.3, 0.5), range(rng, 0.4, 0.55))
        : hsvToRgb(0.3 + (hue - 0.5) * 0.15, range(rng, 0.45, 0.8), range(rng, 0.3, 0.6)),
    hasFlora: terran || kind === "ice",
    ring,
    ...(home ? { home: true } : {}),
  };
}

const MOON_ARCHETYPES: { kind: PlanetArchetype; weight: number }[] = [
  { kind: "barren", weight: 50 },
  { kind: "ice", weight: 24 },
  { kind: "volcanic", weight: 16 },
  { kind: "desert", weight: 10 },
];

/**
 * Moons: small, craggy, mostly airless bodies on inclined orbits around
 * their planet. Same machinery as planets (full terrain, landable, walkable)
 * — only the rolls differ. Orbital placement is STATIC, like planets: the
 * engine's collision/multiplayer contract requires fixed body positions
 * (orbital motion would need the wall-clock standard everywhere terrain
 * math lives; revisit alongside phase 7's lifecycle clock).
 */
function generateMoon(parent: PlanetDef, index: number): PlanetDef {
  const seed = `${parent.seed}:moon:${index}`;
  const rng = rngFromString(seed);

  const radius = range(rng, 280, 820) * WORLD_SCALE;
  // Each moon a shell further out, on its own tilted plane. (orbitR rides
  // parent.radius, which is already WORLD_SCALE'd — no extra factor here.)
  const orbitR = parent.radius * (range(rng, 2.6, 3.6) + index * range(rng, 1.5, 2.3));
  let mx = rng() * 2 - 1;
  let my = (rng() * 2 - 1) * 0.4; // flattened toward the ecliptic
  let mz = rng() * 2 - 1;
  const ml = pmath.hypot(mx, my, mz) || 1;
  mx /= ml; my /= ml; mz /= ml;
  const position: [number, number, number] = [
    parent.position[0] + mx * orbitR,
    parent.position[1] + my * orbitR,
    parent.position[2] + mz * orbitR,
  ];

  const totalW = MOON_ARCHETYPES.reduce((s2, a) => s2 + a.weight, 0);
  let roll = rng() * totalW;
  let kind: PlanetArchetype = "barren";
  for (const a of MOON_ARCHETYPES) {
    roll -= a.weight;
    if (roll <= 0) {
      kind = a.kind;
      break;
    }
  }
  const hue = rng();

  const terrain: TerrainDef = {
    amplitudeRel: range(rng, 0.04, 0.07), // craggier than planets (mountain boost in sample() adds the drama)
    continentFreq: range(rng, 1.1, 2.2),
    mountainFreq: range(rng, 3, 6),
    detailFreq: range(rng, 150, 300),
    ridginess: range(rng, 0.5, 1),
    warpAmp: range(rng, 0.15, 0.35),
    hillFreq: range(rng, 4, 8),
    riverDensity: 0,
    riverFreq: 3,
    plateau: kind === "volcanic" ? range(rng, 0.3, 0.6) : range(rng, 0, 0.2),
  };
  const climate: ClimateDef = {
    temp: kind === "ice" ? range(rng, -0.95, -0.6) : kind === "volcanic" ? range(rng, 0.5, 0.9) : range(rng, -0.4, 0.2),
    moist: range(rng, -0.8, -0.3),
    latitude: range(rng, 0.4, 0.9),
    snowline: kind === "ice" ? 0.05 : 3,
  };
  const amplitude = radius * terrain.amplitudeRel;
  const seaRadius =
    kind === "volcanic" && rng() > 0.7 ? radius + amplitude * range(rng, -0.25, -0.1) : null;
  const palette = buildPalette(kind, hue, rng);
  const tiltA = range(rng, 0, Math.PI * 2);
  const tilt = range(rng, 0, 0.6);
  const spin: SpinDef = {
    axis: [pmath.sin(tilt) * pmath.cos(tiltA), pmath.cos(tilt), pmath.sin(tilt) * pmath.sin(tiltA)],
    periodS: range(rng, 1800, 3400), // small bodies still turn faster (30-57 min days)
    phase: range(rng, 0, Math.PI * 2),
  };
  // Moons circle their planet within a session (25-70 min).
  const orbit: OrbitDef = {
    periodS: range(rng, 3800, 10500),
    normal: (() => {
      const n: [number, number, number] = [mx * 0.25, 1, mz * 0.25];
      const l = pmath.hypot(n[0], n[1], n[2]);
      return [n[0] / l, n[1] / l, n[2] / l] as [number, number, number];
    })(),
  };
  const hasAtmosphere = rng() > 0.85; // thin and rare
  const roman = ["I", "II", "III", "IV"][index] ?? `${index + 1}`;

  return {
    id: seed,
    seed,
    name: `${parent.name} ${roman}`,
    radius,
    position,
    archetype: kind,
    terrain,
    climate,
    spin,
    orbit,
    palette,
    seaRadius,
    seaColor: kind === "volcanic" ? [1, 0.32, 0.08] : hsvToRgb(0.55, 0.3, 0.7),
    atmosphere: hasAtmosphere
      ? { color: hsvToRgb(0.5 + hue * 0.15, range(rng, 0.2, 0.5), range(rng, 0.6, 0.85)), height: radius * range(rng, 0.05, 0.09) }
      : null,
    scatterColor:
      kind === "ice"
        ? hsvToRgb(0.55, range(rng, 0.2, 0.4), range(rng, 0.6, 0.8))
        : hsvToRgb(hue, range(rng, 0.1, 0.3), range(rng, 0.3, 0.5)),
    hasFlora: kind === "ice",
    parentId: parent.id,
  };
}

/** Generate the star system at integer galaxy coordinates. */
export function generateSystem(gx: number, gy: number, gz: number): SystemDef {
  const id = `${gx}:${gy}:${gz}`;
  return generateSystemAt(id, `hyperlight:sys:${id}`, regionAt(gx, gy, gz));
}

// ── Sealed frontier systems ─────────────────────────────────────────────────

/** New coordinates beyond this Chebyshev radius use the server-revealed v2
 *  seed namespace. The old inner lattice remains byte-stable forever. */
export const SEALED_FRONTIER_RADIUS = 12;

const SEALED_ID_RE = /^sealed:(-?\d{1,4}):(-?\d{1,4}):(-?\d{1,4})$/;

export function isSealedFrontier(gx: number, gy: number, gz: number): boolean {
  return Math.max(Math.abs(gx), Math.abs(gy), Math.abs(gz)) > SEALED_FRONTIER_RADIUS;
}

export function sealedSystemId(gx: number, gy: number, gz: number): string {
  return `sealed:${gx}:${gy}:${gz}`;
}

/** Canonical id used by navigation for a lattice coordinate. */
export function systemIdAt(gx: number, gy: number, gz: number): string {
  return isSealedFrontier(gx, gy, gz) ? sealedSystemId(gx, gy, gz) : `${gx}:${gy}:${gz}`;
}

/** True when an id uses the one canonical namespace for its coordinates.
 *  This prevents callers from spelling a frontier coordinate as a legacy
 *  lattice id (or a core coordinate as sealed) to bypass release policy. */
export function isCanonicalSystemId(id: unknown): boolean {
  const ref = parseSystemRef(id);
  if (!ref) return false;
  // Home pockets and cache instances have no lattice spelling at all: their
  // grammars are anchored and exact, so parsing IS canonicality.
  if (ref.kind === "home" || ref.kind === "cache") return true;
  return systemIdAt(...ref.c) === id;
}

/** Public pre-discovery shell. It deliberately exposes no planet roster,
 *  habitability, resources, settlements, or true generated system name. */
export function sealedSystemStub(gx: number, gy: number, gz: number): {
  id: string;
  name: string;
  star: StarDef;
} {
  const id = sealedSystemId(gx, gy, gz);
  const rng = rngFromString(`hyperlight:sealed-stub:v1:${gx}:${gy}:${gz}`);
  const cls = STAR_CLASSES[Math.floor(rng() * STAR_CLASSES.length)] ?? STAR_CLASSES[0];
  return {
    id,
    name: `Unresolved ${gx}:${gy}:${gz}`,
    star: {
      seed: `hyperlight:sealed-stub:v1:${gx}:${gy}:${gz}:star`,
      color: cls.color,
      radius: range(rng, 13000, 22000) * WORLD_SCALE,
      intensity: range(rng, 1.6, 2.4),
    },
  };
}

/** Materialize a sealed v2 system after the server releases its 256-bit
 *  opaque seed. Nothing else in this function is secret; secrecy comes from
 *  the high-entropy seed never being present in an undiscovered client. */
export function generateSealedSystem(
  gx: number,
  gy: number,
  gz: number,
  revealedSeed: string,
): SystemDef {
  if (!/^[A-Za-z0-9_-]{43}$/.test(revealedSeed)) throw new Error("invalid sealed-system seed");
  const id = sealedSystemId(gx, gy, gz);
  return generateSystemAt(id, `hyperlight:sealed:v1:${revealedSeed}`, regionAt(gx, gy, gz));
}

/** Process-local reveal cache. Server and browser each populate their own
 *  copy only after authorization; ordinary public lattice generation never
 *  enters it. */
const SEALED_DEFS = new Map<string, SystemDef>();
const SEALED_DEF_CACHE_MAX = 256;

export function registerSealedSystem(id: string, revealedSeed: string): SystemDef | null {
  const m = SEALED_ID_RE.exec(id);
  if (!m) return null;
  const def = generateSealedSystem(Number(m[1]), Number(m[2]), Number(m[3]), revealedSeed);
  SEALED_DEFS.delete(id);
  SEALED_DEFS.set(id, def);
  while (SEALED_DEFS.size > SEALED_DEF_CACHE_MAX) {
    const oldest = SEALED_DEFS.keys().next().value as string | undefined;
    if (oldest === undefined) break;
    SEALED_DEFS.delete(oldest);
  }
  return def;
}

export function hasSealedSystemReveal(id: string): boolean {
  return SEALED_DEFS.has(id);
}

// ── C4 home systems ──────────────────────────────────────────────────────────

/** Home-system uid charset: Firebase uids (alnum) plus -/_ — NO ':' so the
 *  two id forms can never collide, and NO '.' so the uid can never swallow the
 *  character suffix. Multi-character accounts (2026-08-03) append `.c<slot>`:
 *  `home:<uid>` is the legacy/account form, `home:<uid>.<cid>` is a specific
 *  character's home. '.' is unambiguous because uids never contain it. */
const HOME_ID_RE = /^home:([A-Za-z0-9_-]{1,64})(?:\.(c\d{1,3}))?$/;

/** `home:<uid>` for a verified uid, or `home:<uid>.<cid>` for one character. */
export function homeSystemId(uid: string, cid?: string): string {
  return cid ? `home:${uid}.${cid}` : `home:${uid}`;
}

/** The owner ACCOUNT uid of a home-system id, or null if `id` isn't one.
 *  Deliberately cid-blind: every privacy gate compares this against the
 *  verified session uid, and any character of the owning account may enter
 *  any of that account's home pockets. */
export function homeOwner(id: string): string | null {
  const m = HOME_ID_RE.exec(id);
  return m ? m[1] : null;
}

/** The character id (`c<slot>`) of a home-system id, or null for the legacy
 *  account-wide form / non-home ids. */
export function homeCharacter(id: string): string | null {
  const m = HOME_ID_RE.exec(id);
  return m?.[2] ?? null;
}

// ── Cache instances (instanced procedural dungeons) ─────────────────────────

/**
 * Cache-instance id grammar: `cache:<site>:<key>:<epoch>`.
 *
 * - `<site>`  opaque site token — planet id + site index, spelled WITHOUT
 *             colons (e.g. `p0.0.0.2.5`), because ':' is the id's separator.
 * - `<key>`   instance key: a party id, or the owner uid when solo. Server-
 *             resolved; a client never gets to name it.
 * - `<epoch>` reroll counter (non-negative integer). A fresh epoch at the same
 *             site is a different dungeon — layouts are f(site, epoch).
 *
 * Tokens share the home-uid charset plus '.', minus ':' — so a cache id can
 * never collide with the lattice / sealed / home grammars.
 */
const CACHE_TOKEN_RE = /^[A-Za-z0-9_.-]{1,64}$/;
const CACHE_ID_RE = /^cache:([A-Za-z0-9_.-]{1,64}):([A-Za-z0-9_.-]{1,64}):(\d{1,15})$/;

/** True when `t` may be used as a cache `<site>` or `<key>` token. */
export function isCacheToken(t: unknown): t is string {
  return typeof t === "string" && CACHE_TOKEN_RE.test(t);
}

/** Build the canonical id of one cache instance. Throws on tokens that would
 *  produce an unparseable (therefore unjoinable) id — fail loud, never mint a
 *  room nobody can reach. */
export function cacheSystemId(site: string, key: string, epoch: number): string {
  if (!isCacheToken(site)) throw new Error(`cacheSystemId: invalid site token "${site}"`);
  if (!isCacheToken(key)) throw new Error(`cacheSystemId: invalid key token "${key}"`);
  if (!Number.isInteger(epoch) || epoch < 0 || epoch > 999_999_999_999_999) {
    throw new Error(`cacheSystemId: invalid epoch ${epoch}`);
  }
  return `cache:${site}:${key}:${epoch}`;
}

/**
 * Discriminated system-id parser: legacy lattice `"x:y:z"`, sealed frontier
 * `"sealed:x:y:z"`, private `"home:<uid>"`, or a cache instance
 * `"cache:<site>:<key>:<epoch>"`.
 * parseSystemId (protocol.ts) stays lattice-only on purpose — its callers do
 * coordinate arithmetic only for the legacy form; identity callers use this
 * parser (sealed refs also expose coordinates) or
 * {@link systemDefFor}.
 */
export function parseSystemRef(
  id: unknown,
): { kind: "lattice"; c: [number, number, number] }
  | { kind: "sealed"; c: [number, number, number] }
  | { kind: "home"; uid: string; cid?: string }
  | { kind: "cache"; site: string; key: string; epoch: number }
  | null {
  if (typeof id !== "string") return null;
  const home = HOME_ID_RE.exec(id);
  if (home) return { kind: "home", uid: home[1], ...(home[2] ? { cid: home[2] } : {}) };
  const cache = CACHE_ID_RE.exec(id);
  if (cache) return { kind: "cache", site: cache[1], key: cache[2], epoch: Number(cache[3]) };
  const sealed = SEALED_ID_RE.exec(id);
  if (sealed) return { kind: "sealed", c: [Number(sealed[1]), Number(sealed[2]), Number(sealed[3])] };
  if (!/^-?\d{1,4}:-?\d{1,4}:-?\d{1,4}$/.test(id)) return null;
  const [x, y, z] = id.split(":").map(Number);
  return { kind: "lattice", c: [x, y, z] };
}

/**
 * THE string→SystemDef chokepoint: every `generateSystem(...parseSystemId(id))`
 * site routes here so `home:<uid>` ids resolve everywhere identity (not lattice
 * arithmetic) is what's needed. Null for malformed ids — callers keep their
 * existing null behavior (feature disabled for that room/row).
 */
export function systemDefFor(id: unknown): SystemDef | null {
  const ref = parseSystemRef(id);
  if (ref === null) return null;
  if (ref.kind === "home") return generateHomeSystem(ref.uid, ref.cid);
  if (ref.kind === "sealed") return SEALED_DEFS.get(String(id)) ?? null;
  if (ref.kind === "cache") return cachePadSystem(String(id));
  return generateSystem(...ref.c);
}

// ── Cache pad world ─────────────────────────────────────────────────────────

/**
 * Pad-world radius. Curvature drop is d²/2R, so at a tier-1 cache's ~91 m
 * half-extent this is 91²/120000 ≈ 0.069 m — under the 0.15 m budget in the
 * caches plan (combat-lab's R = 2200 would drop 1.9 m and read as a bowl).
 */
export const CACHE_PAD_RADIUS = 60_000;

/** The seed namespace every cache pad def is minted under — see {@link isCachePadDef}. */
const CACHE_SEED_PREFIX = "hyperlight:cache:";

/** Process-local pad defs, one per cache id (SEALED_DEFS' identity contract:
 *  repeat calls hand back the SAME object, so `===` comparisons hold). */
const CACHE_DEFS = new Map<string, SystemDef>();
const CACHE_DEF_CACHE_MAX = 256;

/**
 * THE pad world: the synthetic single-planet system a cache instance is hosted
 * on. Phase 4 binds the renderer to `def.planets[0]` and displaces its surface
 * near the +Y pole from the generated layout — which is why this def is
 * deliberately featureless: a huge dry barren sphere with near-zero relief, no
 * sea, no atmosphere, no flora, and effectively no spin or orbit (million-year
 * periods ⇒ the pose is static, so a cache never rotates out from under a
 * standing player).
 *
 * Hand-built on purpose — `generateSystemAt` would drag in the whole worldgen
 * pipeline (star classes, planet roster, belts, moons) to produce a world whose
 * every generated feature we then have to suppress.
 */
export function cachePadSystem(id: string): SystemDef | null {
  const ref = parseSystemRef(id);
  if (ref === null || ref.kind !== "cache") return null;
  const hit = CACHE_DEFS.get(id);
  if (hit) return hit;

  const seed = `${CACHE_SEED_PREFIX}${id}`;
  const grey = (v: number): [number, number, number] => [v, v * 0.98, v * 0.94];
  const R = CACHE_PAD_RADIUS;
  const planet: PlanetDef = {
    id: `${id}:pad`,
    seed: `${seed}:pad`,
    name: "Cache",
    radius: R,
    /**
     * Off the star, so the pad is lit rather than sitting inside it — and
     * specifically BELOW it on the Y axis, so the direction from the pad planet to
     * the star is +Y. The layout is built at the planet's +Y POLE, so that is the
     * difference between local NOON over the whole dungeon and a permanent grazing
     * terminator: with the old +X placement the pole sat 90° from the star and the
     * cache rendered nearly black. Nothing gameplay-side reads this (the orbit
     * period is a billion seconds, so the pose is static).
     */
    position: [0, -180_000 * WORLD_SCALE, 0],
    archetype: "barren",
    terrain: {
      // 60_000 × 2e-5 = 1.2 m of relief: enough that the sampler is not a
      // degenerate constant, small enough that the layout owns the ground.
      amplitudeRel: 0.00002,
      continentFreq: 1,
      mountainFreq: 2,
      detailFreq: 120,
      ridginess: 0.4,
      warpAmp: 0.1,
      hillFreq: 3,
      riverDensity: 0,
      riverFreq: 3,
      plateau: 0,
    },
    climate: { temp: 0, moist: -0.6, latitude: 0, snowline: 3 },
    // Static pose: periods so long that spinAngle/orbit phase never move
    // measurably within a session.
    spin: { axis: [0, 1, 0], periodS: 1e9, phase: 0 },
    orbit: { periodS: 1e9, normal: [0, 1, 0] },
    palette: {
      table: [
        grey(0.30), grey(0.32), grey(0.34),
        grey(0.34), grey(0.36), grey(0.38),
        grey(0.38), grey(0.40), grey(0.42),
        grey(0.42), grey(0.44), grey(0.46),
      ],
      cliff: grey(0.26),
      sand: grey(0.48),
      snow: grey(0.72),
      water: grey(0.20),
    },
    seaRadius: null,
    seaColor: grey(0.2),
    atmosphere: null,
    scatterColor: grey(0.35),
    hasFlora: false,
  };
  const def: SystemDef = {
    id,
    seed,
    name: `Cache ${ref.site}`,
    star: { seed: `${seed}:star`, color: 0xfff0d8, radius: 9000 * WORLD_SCALE, intensity: 1.7 },
    planets: [planet],
    belts: [],
    region: { density: 0, temp: 0 },
  };

  CACHE_DEFS.set(id, def);
  while (CACHE_DEFS.size > CACHE_DEF_CACHE_MAX) {
    const oldest = CACHE_DEFS.keys().next().value as string | undefined;
    if (oldest === undefined || oldest === id) break;
    CACHE_DEFS.delete(oldest);
  }
  return def;
}

/**
 * True for the synthetic pad planet a cache INSTANCE is hosted on.
 *
 * ⚠ A CACHE PAD IS A STAGE, NOT A WORLD. It is a real {@link PlanetDef} riding the
 * whole planet pipeline (that is the P6b architecture, and it is what makes an
 * instance cost nothing to stand up) — which means every generic per-planet
 * decoration will happily run on it and litter the dungeon with boulders, tufts and
 * flora that belong to no biome anyone chose. Live play (2026-07-31) reported exactly
 * that: rocks poking through cache tunnel floors, "like the dungeon is sitting on some
 * random planet". It is.
 *
 * The test is on the def's OWN identity — its seed namespace — so a call site never
 * has to string-match an id, and any future pad-def variant is covered by construction.
 * `hasFlora: false` is NOT this test: barren worlds set that too and still (rightly)
 * scatter rock.
 */
export function isCachePadDef(def: Pick<PlanetDef, "seed" | "id"> | null | undefined): boolean {
  if (!def) return false;
  return typeof def.seed === "string" && def.seed.startsWith(CACHE_SEED_PREFIX);
}

/**
 * A player's private system (C4): planet 0 is a guaranteed-habitable terran
 * home world (see generatePlanet's `home` branch) bearing a town-tier capital
 * (tierRegistry's home guarantee). Fresh seed namespace `hyperlight:home:<uid>`
 * (per-character: `hyperlight:home:<uid>.<cid>` — every character gets their
 * OWN deterministic home world) — lattice systems are untouched byte-for-byte
 * (digest-gated). The region is a fixed pleasant mid: home has no lattice
 * point for regionAt to sample.
 */
export function generateHomeSystem(uid: string, cid?: string): SystemDef {
  const key = cid ? `${uid}.${cid}` : uid;
  return generateSystemAt(homeSystemId(uid, cid), `hyperlight:home:${key}`, { density: 0.55, temp: 0.05 }, 0);
}

/** Shared generation core; `homePlanet` (default none) forces that planet
 *  index into the guaranteed-habitable home configuration. */
function generateSystemAt(id: string, seed: string, region: RegionDef, homePlanet = -1): SystemDef {
  const rng = rngFromString(seed);
  const regionKey = namingRegionKey(id) ?? seed;
  const name = generatedSystemName(seed, regionKey);

  const totalWeight = STAR_CLASSES.reduce((s, c) => s + c.weight, 0);
  let roll = rng() * totalWeight;
  let starColor = STAR_CLASSES[0].color;
  for (const c of STAR_CLASSES) {
    roll -= c.weight;
    if (roll <= 0) { starColor = c.color; break; }
  }
  const star: StarDef = {
    seed: `${seed}:star`,
    color: starColor,
    radius: range(rng, 13000, 22000) * WORLD_SCALE,
    intensity: range(rng, 1.6, 2.4),
  };

  // Dense cores crowd 7-11 worlds; the frontier runs lean.
  const planetCount = rangeInt(rng, 3, 6) + Math.round(region.density * 4);
  const planets: PlanetDef[] = [];
  const orbits: number[] = [];
  let orbit = range(rng, 70000, 110000) * WORLD_SCALE;
  for (let i = 0; i < planetCount; i++) {
    planets.push(generatePlanet(seed, i, orbit, region.temp, i === homePlanet, name, regionKey));
    orbits.push(orbit);
    orbit += range(rng, 55000, 110000) * WORLD_SCALE;
  }

  // A biased family deck can (rarely) mint the same native name twice in one
  // system; re-roll duplicates deterministically. Lettered designations are
  // unique by construction.
  const taken = new Set([name]);
  planets.forEach((p, i) => {
    if (p.archetype === "barren" && !p.home) return;
    for (let v = 1; taken.has(p.name) && v <= 4; v++) p.name = generatedPlanetName(seed, i, regionKey, v);
    taken.add(p.name);
  });

  // Asteroid belts settle into the gaps between orbits.
  const belts: BeltDef[] = [];
  const beltRng = rngFromString(`${seed}:belts`);
  for (let i = 0; i + 1 < orbits.length; i++) {
    if (beltRng() > 0.22 + region.density * 0.2) continue;
    const mid = (orbits[i] + orbits[i + 1]) / 2;
    let nx = (beltRng() - 0.5) * 0.3;
    let ny = 1;
    let nz = (beltRng() - 0.5) * 0.3;
    const nl = pmath.hypot(nx, ny, nz);
    belts.push({
      seed: `${seed}:belt:${i}`,
      radius: mid,
      width: range(beltRng, 5000, 14000) * WORLD_SCALE,
      thickness: range(beltRng, 1200, 3500) * WORLD_SCALE,
      normal: [nx / nl, ny / nl, nz / nl],
      count: Math.round((420 + Math.floor(beltRng() * 500)) * WORLD_SCALE), // scale with the longer annulus so the ring stays populated
    });
    if (belts.length >= 2) break;
  }

  // Moons: bigger planets host more; every one a real, landable body.
  const moons: PlanetDef[] = [];
  for (const p of planets) {
    const mrng = rngFromString(`${p.seed}:mooncount`);
    const max = p.radius > 3200 ? 4 : p.radius > 2300 ? 3 : 2;
    const min = p.radius > 3000 ? 1 : 0; // giants always keep company
    const n = rangeInt(mrng, min, max);
    for (let i = 0; i < n; i++) moons.push(generateMoon(p, i));
  }
  planets.push(...moons);

  return { id, seed, name, star, planets, belts, region };
}

// ---------------------------------------------------------------------------
// Body poses — where everything IS at wall-clock time t (seconds).
// Pure math shared by client and server: orbits move centers, spins turn
// terrain. Rodrigues rotation, no THREE dependency.
// ---------------------------------------------------------------------------

export function rotateAbout(
  v: [number, number, number],
  axis: [number, number, number],
  angle: number,
): [number, number, number] {
  const [x, y, z] = v;
  const [ax, ay, az] = axis;
  const c = pmath.cos(angle);
  const s = pmath.sin(angle);
  const dot = ax * x + ay * y + az * z;
  return [
    x * c + (ay * z - az * y) * s + ax * dot * (1 - c),
    y * c + (az * x - ax * z) * s + ay * dot * (1 - c),
    z * c + (ax * y - ay * x) * s + az * dot * (1 - c),
  ];
}

export interface BodyPose {
  center: [number, number, number];
  /** Spin angle around def.spin.axis at time t. */
  spinAngle: number;
}

/** Pose of every body in `def.planets` order at wall-clock `t` seconds. */
/**
 * One body's pose without building the whole system's array — hot paths
 * (per-NPC schedule evaluation) need a single planet thousands of times a
 * frame; systemPoses allocates poses for every body per call.
 */
export function planetPoseAt(def: SystemDef, i: number, t: number): BodyPose {
  const p = def.planets[i];
  const th = ((t % p.orbit.periodS) / p.orbit.periodS) * pmath.TWO_PI; // pre-reduction keeps pmath.sin/cos in their accurate range and fixes precision decay at large t.
  const spinAngle = p.spin.phase + ((t % p.spin.periodS) / p.spin.periodS) * pmath.TWO_PI; // pre-reduction keeps pmath.sin/cos in their accurate range and fixes precision decay at large t.
  if (!p.parentId) return { center: rotateAbout(p.position, p.orbit.normal, th), spinAngle };
  const pi = def.planets.findIndex((q) => q.id === p.parentId);
  const parent = def.planets[pi];
  const pc = planetPoseAt(def, pi, t).center;
  const off: [number, number, number] = [
    p.position[0] - parent.position[0],
    p.position[1] - parent.position[1],
    p.position[2] - parent.position[2],
  ];
  const ro = rotateAbout(off, p.orbit.normal, th);
  return { center: [pc[0] + ro[0], pc[1] + ro[1], pc[2] + ro[2]], spinAngle };
}

export function systemPoses(def: SystemDef, t: number): BodyPose[] {
  const poses: BodyPose[] = new Array(def.planets.length);
  const idxOf = new Map<string, number>();
  def.planets.forEach((p, i) => idxOf.set(p.id, i));
  for (let i = 0; i < def.planets.length; i++) {
    const p = def.planets[i];
    if (p.parentId) continue; // moons need their parent first
    const th = ((t % p.orbit.periodS) / p.orbit.periodS) * pmath.TWO_PI; // pre-reduction keeps pmath.sin/cos in their accurate range and fixes precision decay at large t.
    poses[i] = {
      center: rotateAbout(p.position, p.orbit.normal, th),
      // pre-reduction keeps pmath.sin/cos in their accurate range and fixes precision decay at large t.
      spinAngle: p.spin.phase + ((t % p.spin.periodS) / p.spin.periodS) * pmath.TWO_PI,
    };
  }
  for (let i = 0; i < def.planets.length; i++) {
    const m = def.planets[i];
    if (!m.parentId) continue;
    const pi = idxOf.get(m.parentId)!;
    const parent = def.planets[pi];
    const off: [number, number, number] = [
      m.position[0] - parent.position[0],
      m.position[1] - parent.position[1],
      m.position[2] - parent.position[2],
    ];
    const th = ((t % m.orbit.periodS) / m.orbit.periodS) * pmath.TWO_PI; // pre-reduction keeps pmath.sin/cos in their accurate range and fixes precision decay at large t.
    const ro = rotateAbout(off, m.orbit.normal, th);
    const pc = poses[pi].center;
    poses[i] = {
      center: [pc[0] + ro[0], pc[1] + ro[1], pc[2] + ro[2]],
      // pre-reduction keeps pmath.sin/cos in their accurate range and fixes precision decay at large t.
      spinAngle: m.spin.phase + ((t % m.spin.periodS) / m.spin.periodS) * pmath.TWO_PI,
    };
  }
  return poses;
}

// ---------------------------------------------------------------------------
// Terrain sampling — the single source of truth for planet surfaces.
// ---------------------------------------------------------------------------

function smooth01(x: number): number {
  const t = Math.min(1, Math.max(0, x));
  return t * t * (3 - 2 * t);
}

/** One full terrain sample: height plus everything biomes need. */
export interface TerrainSample {
  /** Height in AMPLITUDE units (multiply by sampler.amplitude for meters). */
  h: number;
  /** Native mobile dune sand 0..1, after water/coast/settlement exclusions.
   * Optional for existing fixtures; sample() always writes it. */
  duneSand?: number;
  /** Exposed sandstone landform coverage, after downstream water/settlement edits. */
  butteRock?: number;
  /** Exposed canyon walls/talus. */
  canyonRock?: number;
  /** Canyon floor, talus and shelf substrate/refuge coverage, after water and
   * settlement exclusions. Always written, including climate=false queries. */
  canyonGround?: number;
  /** Finite closed freshwater basin level, metres above base radius. -1e9
   * outside its footprint; dry hummocks may retain a negative water depth. */
  wetlandWaterM?: number;
  /** Native inland basin habitat and exposed shallow-bank sediment. */
  wetlandMask?: number;
  wetlandMud?: number;
  /** Final river-bank substrate and dry low-bank habitat, 0..1. Always reset
   * and written, including climate=false; never changes height or water. */
  riverBankSediment?: number;
  riverBankRock?: number;
  /** Supported dry shelves within a finite cascade formation. */
  riverTerrace?: number;
  /** Static native shelter, 0..1; -1 preserves nonliving/ice snow rules. */
  snowRetention?: number;
  riparian?: number;
  /** Metres of grounded glacier above its rock bed. h includes this solid ice;
   * absent/zero elsewhere. Always written, including climate=false queries. */
  glacierIceM?: number;
  /** Native glacial valley/ridge influence; zero outside its bounded province. */
  glaciation?: number;
  /** Native volcanic-province weight and finite lava surface in metres above
   * base radius. A missing surface is -1e9; lava does not supply solid support. */
  volcanism?: number;
  lavaSurfaceM?: number;
  lavaDepthM?: number;
  /** Sheltered, sea-connected estuary influence; never a separate water plane. */
  estuaryShelter?: number;
  /** Temperature / moisture in [-1, 1] (only when `climate` requested). */
  temp: number;
  moist: number;
  /** Carved water channel strength 0..1 (rivers + lake beds). */
  river: number;
  /** Mountain-mask strength 0..1 (drives scatter / rockiness). */
  mountain: number;
  /** Downstream flow tangent (planet-local unit), 0 off a river — orients ripples. */
  flowx: number;
  flowy: number;
  flowz: number;
  /** Downstream arclength of the nearest river centreline point, metres (0 off
   *  a river). OPTIONAL so the many existing TerrainSample literals stay valid;
   *  sample() always writes it. Drives the continuous flow phase — the old
   *  dot(ppos, flow) seed reset every densify step (~20 m sawtooth). */
  riverS?: number;
  /** River water-surface elevation at the nearest centreline point, AMPLITUDE
   *  units — the plane the ribbon renders and the swim query floats on.
   *  −1e9 when no river influences this direction. Optional like riverS. */
  riverWs?: number;
  /** Local river current speed, m/s (0 off a river). Optional like riverS. */
  riverSpeed?: number;
  /** Grown settlement carve mask 0..1 — suppresses scatter/detail. */
  town: number;
  /** Road-surface SDF, metres: (dist − half) of the nearest carved ROAD feature,
   *  clamped to ±8; +8 = no road nearby. Drives the terrain road PAINT (W2d-3
   *  wrap) — roads are part of the ground, not floating ribbons. */
  road: number;
}

/** Module-scope carve-hit scratch (JS is single-threaded; matches the sampler's
 *  own reusable-scratch pattern — allocation-free on the terrain hot path). */
const CARVE_OUT: CarveHit = { level: 0, k: 0, kind: 0, dist: 0, half: 0, retaining: false };

export class PlanetSampler {
  private readonly riverBankScratch=riverBankSample();
  private alpineShelter:AlpineShelter|null=null;
  private readonly escarpmentScratch=escarpmentSample();
  private warpA: SimplexNoise3D;
  private warpB: SimplexNoise3D;
  private continents: SimplexNoise3D;
  private mountains: SimplexNoise3D;
  private hills: SimplexNoise3D;
  private detail: SimplexNoise3D;
  private basinN: SimplexNoise3D;
  private tempN: SimplexNoise3D;
  private moistN: SimplexNoise3D;
  private localLandforms: LocalLandforms;
  private mountainLandforms: MountainLandforms;
  private regionalLandforms: RegionalLandforms;
  private regionalScratch = regionalSample();
  private duneTerrain:DuneTerrain|null=null;
  private duneScratch=duneSample();
  private butteTerrain:ButteTerrain|null=null;
  private butteScratch=butteSample();
  private canyonTerrain:CanyonTerrain|null=null;
  private canyonScratch=canyonSample();
  private inlandWetlandTerrain:InlandWetlandTerrain|null=null;
  private inlandWetlandScratch=inlandWetlandSample();
  private coastalLandforms: CoastalLandforms | null = null;
  private coastalScratch = coastalSample();
  private estuaryField: EstuaryField | null = null;
  private estuaryScratch = estuarySample();
  private glacialTerrain: GlacialTerrain | null = null;
  private glacialScratch: GlacialSample = { heightM: 0, iceM: 0, weight: 0 };
  private volcanicTerrain: VolcanicTerrain | null = null;
  private volcanicScratch: VolcanicSample = {heightM:0,lavaM:-1e9,lavaDepthM:0,weight:0,flowS:0,cross:0};
  readonly amplitude: number;
  /** Sea level in amplitude units (0 when the planet has no sea). */
  readonly seaRel: number;

  private scratch: TerrainSample = { h: 0, temp: 0, moist: 0, river: 0, mountain: 0, flowx: 0, flowy: 0, flowz: 0, town: 0, road: 8 };
  private scratch2: TerrainSample = { h: 0, temp: 0, moist: 0, river: 0, mountain: 0, flowx: 0, flowy: 0, flowz: 0, town: 0, road: 8 };
  /** Sparse seeded river graph — lazily built on first hydro sample, then cached. */
  private riverGraph: RiverGraph | null = null;
  /** Settlement registry — lazily built on first sample, then cached. */
  private townField: TownField | null = null;
  /** Optional planet-wide settlement carve index (W2b-1). Null → the carve
   *  stage is skipped entirely, so a provider-less sampler is byte-identical
   *  to the pre-W2b-1 terrain (the determinism gate). Attached by RegionService
   *  once grown regions are folded in. */
  private settleIdx: SettlementIndex | null = null;

  constructor(readonly def: PlanetDef) {
    const base = hashSeed(def.seed);
    this.warpA = new SimplexNoise3D(base ^ 0x11);
    this.warpB = new SimplexNoise3D(base ^ 0x22);
    this.continents = new SimplexNoise3D(base ^ 0x1);
    this.mountains = new SimplexNoise3D(base ^ 0x2);
    this.hills = new SimplexNoise3D(base ^ 0x33);
    this.detail = new SimplexNoise3D(base ^ 0x3);
    this.basinN = new SimplexNoise3D(base ^ 0x55);
    this.tempN = new SimplexNoise3D(base ^ 0x66);
    this.moistN = new SimplexNoise3D(base ^ 0x77);
    this.localLandforms = new LocalLandforms(def);
    this.mountainLandforms = new MountainLandforms(def);
    this.amplitude = def.radius * def.terrain.amplitudeRel;
    this.seaRel = def.seaRadius !== null ? (def.seaRadius - def.radius) / this.amplitude : 0;
    this.regionalLandforms = new RegionalLandforms(def,(x,y,z)=>this.continentalH(x,y,z)*this.amplitude);
  }

  /** Shared geographic intent for tools and future habitat consumers. */
  regions(): readonly RegionalLandform[] { return this.regionalLandforms.features; }
  regionalAt(dx:number,dy:number,dz:number,out:RegionalSample):RegionalSample {
    return this.regionalLandforms.sample(dx,dy,dz,this.continentalH(dx,dy,dz)*this.amplitude,out);
  }

  dunes():readonly DuneDef[]{return this.duneField().features;}
  private duneField():DuneTerrain{
    if(this.duneTerrain===null){
      const scratch:TerrainSample={h:0,temp:0,moist:0,river:0,mountain:0,flowx:0,flowy:0,flowz:0,town:0,road:8},region=regionalSample();
      this.duneTerrain=new DuneTerrain(this.def,(x:number,y:number,z:number,out:DuneNativeSample)=>{
        // Native substrate never asks coasts, rivers, towns, specialist geology
        // or dunes to initialize themselves. This breaks every lazy-query cycle.
        this.sample(x,y,z,scratch,true,false,true,true,true,true,true,true,true,true);
        this.regionalAt(x,y,z,region);
        out.heightM=scratch.h*this.amplitude;out.temp=scratch.temp;out.moist=scratch.moist;
        out.mountain=scratch.mountain;out.plateau=region.plateau;return out;
      });
    }
    return this.duneTerrain;
  }

  buttes():readonly ButteDef[]{return this.butteField().features;}
  private butteField():ButteTerrain{
    if(this.butteTerrain===null){
      const scratch:TerrainSample={h:0,temp:0,moist:0,river:0,mountain:0,flowx:0,flowy:0,flowz:0,town:0,road:8},region=regionalSample();
      this.butteTerrain=new ButteTerrain(this.def,this.dunes(),(x,y,z,out)=>{
        // Both dry-country fields inspect native substrate without asking one
        // another, coast descriptors, river routing or settlements to initialize.
        this.sample(x,y,z,scratch,true,false,true,true,true,true,true,true,true,true);
        this.regionalAt(x,y,z,region);
        out.heightM=scratch.h*this.amplitude;out.temp=scratch.temp;out.moist=scratch.moist;
        out.mountain=scratch.mountain;out.plateau=region.plateau;return out;
      });
    }
    return this.butteTerrain;
  }

  canyons():readonly CanyonDef[]{return this.canyonField().features;}
  inlandWetlands():readonly InlandWetlandDef[]{return this.inlandWetlandField().features;}
  private inlandWetlandField():InlandWetlandTerrain{
    if(this.inlandWetlandTerrain===null){
      const scratch={}as TerrainSample,region=regionalSample(),coast=coastalSample();
      const enabled=this.def.atmosphere!==null&&this.def.hasFlora&&(this.def.archetype==='terran'||this.def.archetype==='ocean')&&this.def.terrain.amplitudeRel>=.001;
      const rivers=enabled?this.rivers().rivers:[];
      this.inlandWetlandTerrain=new InlandWetlandTerrain(this.def,(x,y,z,out:InlandWetlandNative)=>{
        // Prior final geography, independent of streamed settlement residency.
        // Upstream drainage skips rivers and thus skips this later basin layer.
        this.sample(x,y,z,scratch,true,false,true,false,false,false,false,false,false,false,true);
        this.regionalAt(x,y,z,region);this.coastalAt(x,y,z,coast);
        out.heightM=scratch.h*this.amplitude;out.temp=scratch.temp;out.moist=scratch.moist;out.mountain=scratch.mountain;
        out.plateau=region.plateau;out.coast=coast.weight;out.dune=scratch.duneSand??0;out.butte=scratch.butteRock??0;
        out.canyon=scratch.canyonGround??0;out.riverWs=scratch.riverWs??-1e9;return out;
      },rivers);
    }return this.inlandWetlandTerrain;
  }
  private canyonField():CanyonTerrain{
    if(this.canyonTerrain===null){
      const scratch:TerrainSample={h:0,temp:0,moist:0,river:0,mountain:0,flowx:0,flowy:0,flowz:0,town:0,road:8},region=regionalSample();
      this.canyonTerrain=new CanyonTerrain(this.def,(x,y,z,out:CanyonNative)=>{
        this.sample(x,y,z,scratch,true,false,true,true,true,true,false,false,false,true);
        this.regionalAt(x,y,z,region);out.heightM=scratch.h*this.amplitude;out.temp=scratch.temp;out.moist=scratch.moist;
        out.plateau=region.plateau;out.butteRock=scratch.butteRock??0;return out;
      },(x,y,z)=>this.continentalH(x,y,z)*this.amplitude);
    }return this.canyonTerrain;
  }

  coasts(): readonly CoastalLandform[] { return this.coastalField().features; }
  estuaries(): readonly EstuaryDef[] { return this.terminalField().features; }
  estuaryAt(dx:number,dy:number,dz:number,out:EstuarySample):EstuarySample{
    return this.terminalField().sample(dx,dy,dz,out);
  }
  private terminalField():EstuaryField{
    if(this.estuaryField===null)this.estuaryField=new EstuaryField(this.def,this.rivers().rivers,
      (x,y,z)=>this.drainageH(x,y,z)*this.amplitude,(x,y,z)=>this.continentalH(x,y,z)*this.amplitude);
    return this.estuaryField;
  }
  coastalAt(dx:number,dy:number,dz:number,out:CoastalSample):CoastalSample {
    return this.coastalField().sample(dx,dy,dz,out);
  }
  private coastalField(): CoastalLandforms {
    if(this.coastalLandforms===null){
      const scratch:TerrainSample={h:0,temp:0,moist:0,river:0,mountain:0,flowx:0,flowy:0,flowz:0,town:0,road:8};
      this.coastalLandforms=new CoastalLandforms(this.def,(x,y,z)=>
        this.sample(x,y,z,scratch,false,false,true,true,true,true,true,false,true,true).h*this.amplitude,
        (x,y,z)=>this.continentalH(x,y,z)*this.amplitude);
    }
    return this.coastalLandforms;
  }

  /**
   * Continentalness spline: one master field remapped into ocean floor →
   * shelf → FLAT coastal plains → inland rise → highlands. Output is in
   * amplitude units, roughly [-0.75, 0.8].
   */
  private spline(c: number): number {
    if (c < -0.18) return -0.32 + (c + 0.18) * 0.52; // open ocean floor
    if (c < 0.05) return -0.32 + smooth01((c + 0.18) / 0.23) * 0.38; // shelf → coast (+0.06)
    // The PLAINS SHELF: most of the land budget is a wide, nearly-flat
    // band — open country is the default, relief is the exception.
    if (c < 0.48) return 0.06 + (c - 0.05) * 0.09; // plains (to ~0.10)
    return 0.1 + smooth01((c - 0.48) / 0.52) * 0.58; // late rise → highlands
  }

  /**
   * The smooth CONTINENTALNESS elevation (amplitude units) — warp + continents
   * + spline ONLY, before mountains/hills/hydro. This is the low-frequency macro
   * slope that genuinely trends to the coast, so the river builder traces down it
   * to place channels (and the terrain is then carved to drain along them). Kept
   * in lockstep with the head of sample() (same warp + continent field).
   */
  continentalH(dx: number, dy: number, dz: number): number {
    const t = this.def.terrain;
    const wf = t.continentFreq * 1.7;
    const wa = t.warpAmp;
    const x = dx + this.warpA.fbm(dx * wf, dy * wf, dz * wf, 2) * wa;
    const y = dy + this.warpB.fbm(dx * wf, dy * wf, dz * wf, 2) * wa;
    const z = dz + this.warpA.fbm((dx + 5.2) * wf, (dy + 1.3) * wf, (dz + 9.7) * wf, 2) * wa;
    const cf = t.continentFreq;
    return this.spline(this.continents.fbm(x * cf, y * cf, z * cf, 4));
  }

  /** The real terrain rivers must drain: mountains, hills, coasts and lake
   * basins included; river and settlement edits excluded to avoid recursion. */
  drainageH(dx: number, dy: number, dz: number): number {
    return this.sample(dx, dy, dz, this.scratch, false, false, true, true).h;
  }

  /** Features generated from native coastal gradients, never from streamed
   * regions or renderer bookmarks. Client, workers and server agree. */
  glaciers(): readonly GlacierDef[] { return this.glacialField().features; }

  volcanoes(): readonly VolcanoDef[] { return this.volcanicField().features; }

  private volcanicField(): VolcanicTerrain {
    if(this.volcanicTerrain===null) {
      const scratch:TerrainSample={h:0,temp:0,moist:0,river:0,mountain:0,flowx:0,flowy:0,flowz:0,town:0,road:8};
      this.volcanicTerrain=new VolcanicTerrain(this.def,(x,y,z)=>
        this.sample(x,y,z,scratch,false,false,true,true,true,true).h*this.amplitude);
    }
    return this.volcanicTerrain;
  }

  /** Fast footprint query for drainage exclusion. Thickness does not depend on
   * river carving and does not require recursively sampling the whole terrain. */
  glacierIceAt(dx:number,dy:number,dz:number):number {
    return this.def.archetype==='ice'?this.glacialField().sample(dx,dy,dz,0,this.glacialScratch).iceM:0;
  }

  /** Conservative molten footprint for river routing, without recursion through
   * the river graph. Includes the cooling tip even where rock meets the surface. */
  lavaFootprintAt(dx:number,dy:number,dz:number):boolean {
    return this.def.archetype==='volcanic'&&this.volcanicField().sample(dx,dy,dz,0,this.volcanicScratch).lavaM>-1e8;
  }

  private glacialField(): GlacialTerrain {
    if(this.glacialTerrain===null) {
      const scratch:TerrainSample={h:0,temp:0,moist:0,river:0,mountain:0,flowx:0,flowy:0,flowz:0,town:0,road:8};
      this.glacialTerrain=new GlacialTerrain(this.def,(x,y,z)=>
        this.sample(x,y,z,scratch,false,false,true,true,true).h*this.amplitude);
    }
    return this.glacialTerrain;
  }

  /** The river graph for this planet, built (deterministically) on first use. */
  rivers(): RiverGraph {
    if (this.riverGraph === null) this.riverGraph = buildRiverGraph(this);
    return this.riverGraph;
  }

  /**
   * Retired TownDef compatibility registry. It no longer contributes terrain pads,
   * collision, resource exclusion, rendering, NPCs, or quests. New runtime code must
   * consume region settlements instead; this accessor remains only for old fixtures.
   * @deprecated Use worldgen region settlement APIs.
   */
  towns(): TownField {
    if (this.townField === null) this.townField = townFieldFor(this.def, this);
    return this.townField;
  }

  /**
   * Attach (or detach with `null`) the settlement carve index that the
   * {@link sample} `settlementCarve` stage flattens the terrain toward. Detaching
   * (null) fully removes the stage, restoring byte-identical pre-W2b-1 terrain —
   * so a provider-less sampler stays on the determinism digests.
   */
  setSettlementIndex(idx: SettlementIndex | null): void {
    this.settleIdx = idx;
  }

  /**
   * The full sample. `climate=false` (collision paths) skips the biome
   * fields; meshing/scatter pass true. Pure and allocation-free.
   */
  sample(
    dx: number,
    dy: number,
    dz: number,
    out: TerrainSample,
    climate: boolean,
    skipHydro = false,
    skipTowns = false,
    skipRivers = false,
    skipGlaciers = false,
    skipVolcanoes = false,
    skipCoasts = false,
    skipDunes = false,
    skipButtes = false,
    skipCanyons = false,
    skipWetlands = false,
  ): TerrainSample {
    const t = this.def.terrain;

    // --- Domain warp: bend the sample space so landforms flow ---
    const wf = t.continentFreq * 1.7;
    const wa = t.warpAmp;
    const x = dx + this.warpA.fbm(dx * wf, dy * wf, dz * wf, 2) * wa;
    const y = dy + this.warpB.fbm(dx * wf, dy * wf, dz * wf, 2) * wa;
    const z = dz + this.warpA.fbm((dx + 5.2) * wf, (dy + 1.3) * wf, (dz + 9.7) * wf, 2) * wa;

    // --- Continentalness through the spline ---
    const cf = t.continentFreq;
    const c = this.continents.fbm(x * cf, y * cf, z * cf, 4);
    let h = this.spline(c);
    const land = smooth01((h - 0.0) / 0.1); // 0 at sea level, 1 inland
    const region=this.regionalLandforms.sample(dx,dy,dz,h*this.amplitude,this.regionalScratch);
    const regionWeight=region.weight,plateauIntent=region.plateau,quietFloor=regionWeight*Math.max(region.valley*.78,region.plateau*.88);

    // --- Mountains: ridged, confined by a broad placement mask ---
    const mf = t.mountainFreq;
    const mm = this.mountains.fbm(x * mf * 0.22, y * mf * 0.22, z * mf * 0.22, 2);
    let mask = smooth01((mm - 0.16) / 0.5) * land;
    let mtn = 0;
    if (mask > 0.02 && regionWeight < 1) {
      let r:number;
      if(this.mountainLandforms.enabled)r=this.mountainLandforms.profile(x*mf,y*mf,z*mf);
      else {r=Math.min(1,this.mountains.ridged(x*mf,y*mf,z*mf,4));r=r*r*(3-2*r);}
      // mask² (not mask) gives mountains a CLEAN footprint: the steep ridged
      // detail is confined to the range cores, while the skirts fade to flat —
      // so the open fields between ranges stay genuinely flat instead of carrying
      // a bumpy mountain fringe.
      mtn = r * mask * mask;
      // Mountains tower well above the (deliberately gentle) base relief — this
      // is where the "vaster mountains" live, confined to the placement mask so
      // it never steepens the open plains or the coastline around them.
      h += mtn * (1.0 + t.ridginess * 1.3); // slightly gentler peaks on average (was 1.2 + r*1.5)
    }
    // Regional ranges replace the old macro relief within their footprint.
    // Raising a mountain on top of it would leave the noisy old ridge blocking
    // the valley. Coast/lake, river and settlement stages still run afterward.
    h+=(region.heightM/this.amplitude-h)*regionWeight;
    mask+=(region.mountain-mask)*regionWeight;

    // --- Rolling hills everywhere on land ---
    // Hills + the fine grain below are held to ABSOLUTE metres (hence the
    // `/ this.amplitude`), NOT a fraction of the body's amplitude. Otherwise the
    // larger worlds scale their plains bumpiness up with the mountains and
    // "flat" fields stop reading as flat. Hills are LONG-wavelength (~1–4 km
    // features here), so this ~42 m gives the open fields a gentle, natural roll
    // — visible variation underfoot without the short-scale bumpiness.
    const hf = t.hillFreq;
    h += this.hills.fbm(x * hf, y * hf, z * hf, 3) * (42 / this.amplitude) * (0.4 + 0.6 * land) * (1 - mask * 0.6) * (1-quietFloor);

    // --- Mesas / lava terraces ---
    if (t.plateau > 0.05 && h > 0.1) {
      const step = 0.16;
      const k = h / step;
      const f = k - Math.floor(k);
      const terraced = (Math.floor(k) + smooth01((f - 0.25) / 0.5)) * step;
      h += (terraced - h) * t.plateau * land * (1-regionWeight);
    }

    // --- Gentle coasts: ease the immediate shoreline flat so land rises out of
    // the water as a beach / tidal flat instead of a cliff. Absolute-metre band
    // (hence `/ this.amplitude`) keyed off THIS body's own sea level, so it stays
    // archetype-agnostic and only touches the first few-dozen metres above water
    // — the broad inland plains (well above the band) are untouched. ---
    if (this.def.seaRadius !== null) {
      const band = 280 / this.amplitude; // shoreline band thickness (m → amplitude units)
      const above = h - this.seaRel;
      if (above > 0 && above < band) {
        // Reduce the immediate shore to ~30% of its natural grade (a beach),
        // easing back to full slope at the band top — mild, so it gentles the
        // waterline without the flat-strip / steep-bank artifact a hard crush gives.
        h = this.seaRel + above * (0.3 + 0.7 * smooth01(above / band));
      }
    }

    // --- Lakes: basin depressions in the plains, carved below sea level ---
    let lake = 0;
    if (!skipHydro && this.def.seaRadius !== null && h > 0.02 && h < 0.22 && mask < 0.3) {
      const b = this.basinN.fbm(x * 1.9, y * 1.9, z * 1.9, 2);
      if (b < -0.32) {
        // The old eligibility box switched a deep basin off in one sample at
        // h=.02/.22 or mask=.3, leaving vertical contour walls in mountain
        // foothills. Fade the complete incision through those boundaries.
        const envelope=t.amplitudeRel>=.001&&this.def.archetype!=='ice'&&this.def.archetype!=='volcanic'
          ? smooth01((h-.02)/.045)*smooth01((.22-h)/.06)*smooth01((.3-mask)/.15) : 1;
        const lk = smooth01((-0.32 - b) / 0.2)*envelope;
        const bed = this.seaRel - 0.05;
        h += (bed - h) * lk;
        // Lakes are rendered by the SEA SPHERE (their bed is carved below sea
        // level), NOT by the terrain river-water shader. The basin field is
        // low-frequency, so its broad apron — the ring that stays ABOVE sea —
        // would otherwise be painted as partial shallow water, reading as wide
        // glowing teal washes fanning across the dry land around every lake.
        // Keep it out of `river` (the wet render mask); only lushen moisture.
        lake = lk;
      }
    }

    // Human-scale hills, hollows and short shelf faces fill the scale between
    // the kilometre hills and fine grain. Run after coast/lake shaping, with a
    // continuous shoreline envelope, and BEFORE drainage and settlement edits.
    // Rivers, workers, character support and roads all sample this same relief.
    h += this.localLandforms.offset(dx,dy,dz,h*this.amplitude)/this.amplitude * (1-quietFloor);

    // Include small terrain grain before routing/carving so the incision limit
    // applies to the complete terrain. Channel and settlement blends smooth it.
    const df = t.detailFreq;
    h += this.detail.fbm(dx * df, dy * df, dz * df, 2) * (1.1 / this.amplitude);

    // Connected sand crests replace local substrate detail only inside finite
    // native warm/dry provinces. Coasts, rivers and settlements still own the
    // final surface. Reuse climate noise below when meshing requests it.
    let duneSand=0,duneClimateReady=false,duneTempNoise=0,duneMoistNoise=0;
    if(!skipDunes&&t.amplitudeRel>=.001&&this.def.atmosphere!==null&&
      (this.def.archetype==='desert'||((this.def.archetype==='terran'||this.def.archetype==='ocean')&&this.def.hasFlora))){
      const dunes=this.duneField();
      if(dunes.hasCandidates(dx,dy,dz)){
        const cl=this.def.climate,lat=Math.abs(dy);
        duneTempNoise=this.tempN.fbm(dx*1.3,dy*1.3,dz*1.3,2)*.3;
        duneMoistNoise=this.moistN.fbm(dx*1.6,dy*1.6,dz*1.6,2)*.55;duneClimateReady=true;
        const temp=cl.temp-lat*lat*cl.latitude*1.5-Math.max(0,h)*.55+duneTempNoise;
        const moist=cl.moist+duneMoistNoise+(1-land)*.25+lake*.3;
        const dune=dunes.sample(dx,dy,dz,h*this.amplitude,temp,moist,mask,plateauIntent,this.duneScratch);
        if(dune.weight>0){h=dune.heightM/this.amplitude;duneSand=dune.sand;}
      }
    }

    // Sparse sandstone table groups rise out of native dry ground. Their
    // indexed bounds reject most queries before any additional climate work.
    let butteRock=0;
    if(!skipButtes&&t.amplitudeRel>=.001&&this.def.atmosphere!==null&&
      (this.def.archetype==='desert'||((this.def.archetype==='terran'||this.def.archetype==='ocean')&&this.def.hasFlora))){
      const buttes=this.butteField();
      if(buttes.hasCandidates(dx,dy,dz)){
        if(!duneClimateReady){
          duneTempNoise=this.tempN.fbm(dx*1.3,dy*1.3,dz*1.3,2)*.3;
          duneMoistNoise=this.moistN.fbm(dx*1.6,dy*1.6,dz*1.6,2)*.55;duneClimateReady=true;
        }
        const cl=this.def.climate,lat=Math.abs(dy);
        const temp=cl.temp-lat*lat*cl.latitude*1.5-Math.max(0,h)*.55+duneTempNoise;
        const moist=cl.moist+duneMoistNoise+(1-land)*.25+lake*.3;
        const butte=buttes.sample(dx,dy,dz,h*this.amplitude,temp,moist,mask,plateauIntent,this.butteScratch);
        if(butte.weight>0){h=butte.heightM/this.amplitude;butteRock=butte.rock;duneSand*=1-butteRock;}
      }
    }

    // Selected native ocean reaches replace the uniform eased shoreline with
    // a sand pocket, rocky headlands and cliff backing. The entire seabed and
    // land profile is shared; rivers and settlements retain final authority.
    if(!skipCoasts&&this.def.seaRadius!==null&&t.amplitudeRel>=.001
      &&this.def.archetype!=='ice'&&this.def.archetype!=='volcanic'){
      const coast=this.coastalField().sample(dx,dy,dz,this.coastalScratch);
      h+=(coast.heightM/this.amplitude-h)*coast.weight;
      duneSand*=1-coast.weight;
      butteRock*=1-coast.weight;
    }

    // Connected incision follows stable native coastal geography and precedes
    // final river routing. Its constructor never asks the river graph to exist.
    let canyonRock=0,canyonGround=0;
    if(!skipCanyons&&t.amplitudeRel>=.001&&this.def.atmosphere!==null&&
      (this.def.archetype==='desert'||((this.def.archetype==='terran'||this.def.archetype==='ocean')&&this.def.hasFlora))){
      const canyon=this.canyonField().sample(dx,dy,dz,h*this.amplitude,this.canyonScratch);
      if(canyon.weight>0){h=canyon.heightM/this.amplitude;canyonRock=canyon.rock;canyonGround=canyon.weight;duneSand*=1-Math.max(canyonRock,canyonGround);butteRock*=1-canyon.weight;}
    }

    // Glacial geology precedes river routing and settlement support. Ice is
    // part of the shared solid heightfield; the sea remains open water.
    let glacierIceM=0,glaciation=0;
    if(!skipGlaciers && this.def.archetype==='ice') {
      const g=this.glacialField().sample(dx,dy,dz,h*this.amplitude,this.glacialScratch);
      if(g.weight>0)h=g.heightM/this.amplitude;
      glacierIceM=g.iceM;
      glaciation=g.weight;
    }

    let volcanism=0,lavaSurfaceM=-1e9,lavaDepthM=0;
    if(!skipVolcanoes&&this.def.archetype==='volcanic') {
      const v=this.volcanicField().sample(dx,dy,dz,h*this.amplitude,this.volcanicScratch);
      if(v.weight>0)h=v.heightM/this.amplitude;
      volcanism=v.weight;lavaSurfaceM=v.lavaM;lavaDepthM=v.lavaDepthM;
    }

    // Rivers follow native drainage. Incise only the bounded channel footprint;
    // publish the same surface and flow for water meshes and swimming queries.
    let river = 0;
    let fx = 0;
    let fy = 0;
    let fz = 0;
    let riverS = 0;
    let riverWs = -1e9;
    let riverSpeed = 0;
    let bankMoisture = 0;
    let estuaryShelter = 0;
    let riverBankHit:RiverHit|null=null;
    let terraceRock=0,riverTerrace=0;
    if (!skipHydro && !skipRivers && glacierIceM <= 0 && lavaDepthM <= 0 && this.def.seaRadius !== null && t.riverDensity > 0) {
      const graph=this.rivers(),originalH=h;
      const terrace=sampleRiverEscarpments(graph.escarpments,this.def.radius,this.amplitude,dx,dy,dz,h*this.amplitude,this.escarpmentScratch);
      const terraceActive=terrace.weight>0;
      if(terraceActive){h=terrace.heightM/this.amplitude;terraceRock=terrace.rock;riverTerrace=terrace.terrace;}
      const hit = graph.carveAt(dx, dy, dz);
      if (hit !== null) {
        riverBankHit=hit;
        h = carveRiverHeight(h, hit, this.amplitude);
        if(terraceActive)h=Math.max(originalH-ESCARPMENT_MAX_CUT_M/this.amplitude,h);
        if (climate) bankMoisture = riverBankMoisture(h, hit, this.amplitude);
        river = Math.max(river, 1 - smooth01(hit.dist / hit.waterHalf)); // wet width
        fx = hit.fx;
        fy = hit.fy;
        fz = hit.fz;
        riverS = hit.s;
        // The water CLAIM stops at the carve width — the channel that actually
        // holds water. Claiming ws across the whole valley band invents
        // invisible swimmable water over the DOWNHILL shoulder wherever a river
        // hugs a hillside (terrain there sits below ws but holds no river).
        if (hit.dist <= hit.carveHalf) {
          riverWs = hit.ws;
          riverSpeed = hit.speed;
        }
      }
      if(this.def.archetype!=='ice'&&this.def.archetype!=='volcanic'&&t.amplitudeRel>=.001){
        const e=this.terminalField().sample(dx,dy,dz,this.estuaryScratch);
        estuaryShelter=e.shelter;
        // Bounded sediment deposition/incision at a sea-level outlet. Preserve
        // the river's wet core, then let settlements retain final authority.
        const delta=Math.max(-7,Math.min(36,e.heightM-h*this.amplitude))*e.weight;
        // Feather deposited banks out from the protected wet core; a hard
        // width cutoff would create an unmeshable vertical seam at the mouth.
        const bank=hit?Math.max(0,Math.min(1,(hit.dist-hit.waterHalf)/(hit.carveHalf-hit.waterHalf))):1;
        h+=(delta>0?delta*bank*bank*(3-2*bank):delta)/this.amplitude;
      }
    }

    // Closed inland basins are downstream of existing river geography. Their
    // constructors exclude those corridors; drainage never samples this layer.
    let wetlandWaterM=-1e9,wetlandMask=0,wetlandMud=0;
    if(!skipWetlands&&!skipHydro&&!skipRivers&&t.amplitudeRel>=.001&&this.def.atmosphere!==null&&this.def.hasFlora&&(this.def.archetype==='terran'||this.def.archetype==='ocean')){
      const wetland=this.inlandWetlandField().sample(dx,dy,dz,h*this.amplitude,this.inlandWetlandScratch);
      if(wetland.mask>0){h=wetland.heightM/this.amplitude;wetlandWaterM=wetland.waterM;wetlandMask=wetland.mask;wetlandMud=wetland.mud;}
    }

    // Settlement occupancy is now supplied solely by the CityGraph carve index
    // below. The retired TownDef flat-pad stage no longer mutates terrain.
    let town = 0;

    // --- Settlement carve (W2b-1): flatten the terrain toward grown ROAD beds
    // and PARCEL platforms held in the optional SettlementIndex. Runs AFTER the
    // hydro stage, so a carved road/parcel is settled dry ground. Null index or
    // skipTowns skips the stage, keeping provider-less sampling byte-identical. ---
    let roadSd = 8;
    if (this.settleIdx !== null && !skipTowns && lavaDepthM<=0 && this.settleIdx.carveAt(dx, dy, dz, h, CARVE_OUT)) {
      const k = CARVE_OUT.k;
      const priorH=h;
      h += (CARVE_OUT.level - h) * k;
      // A cut can expose bedrock; a built-up parcel owns its foundation.
      glacierIceM=Math.max(0,glacierIceM+Math.min(0,(h-priorH)*this.amplitude))*(1-k);
      // Road-surface SDF for the terrain paint: roads (kind < 100) plus OPEN
      // MARKET-SQUARE parcels — plazas and market squares are PAVED ground
      // (W2d-4). 108 = 100 + USE_MARKET_SQ; inlined because a value import
      // from worldgen/citygraph would create a runtime cycle (USE_* codes are
      // append-only and never renumbered — see the citygraph registry).
      // Cliff gate (post-W2d-4): on a cliff the carve eases k → 0 (no bed is
      // cut), so painting there would draw a road straight up the rock face —
      // the paint follows the BED, not the graph. k ≈ 1 everywhere a real bed
      // exists (the outer blend keeps k ≥ ~0.9 within the painted half-width).
      if (k > 0.25) {
        const sd = CARVE_OUT.road ?? (CARVE_OUT.kind < 100 ? CARVE_OUT.dist - CARVE_OUT.half : 8);
        roadSd = sd < -8 ? -8 : sd > 8 ? 8 : sd;
      }
      if (CARVE_OUT.kind === 108) {
        // Parcel hits report dist 0 inside, edge distance in the blend band.
        roadSd = Math.min(roadSd,CARVE_OUT.dist <= 0 ? -8 : Math.min(8, CARVE_OUT.dist - 1.2));
      }
      town = Math.max(town, k); // same scatter/detail suppression as a pad
      // Settled ground is dry ground: scale the wet mask + flow down by k, as
      // the pad stage does — a carved bed must not paint water across a street.
      river *= 1 - k;
      fx *= 1 - k;
      fy *= 1 - k;
      fz *= 1 - k;
      riverSpeed *= 1 - k; // settled ground: no current across a carved street
    }


    out.h = h;
    // A settled carve never acquires hidden water. Its feathered perimeter
    // suppresses habitat while any actual carve excludes this closed water.
    out.wetlandWaterM=town>0?-1e9:wetlandWaterM;
    out.wetlandMask=wetlandMask*(1-town);
    out.wetlandMud=wetlandMud*(1-town);
    out.duneSand=duneSand*(1-river)*(1-estuaryShelter)*(1-town)*
      (this.def.seaRadius===null?1:smooth01((h-this.seaRel)*this.amplitude/3));
    out.butteRock=butteRock*(1-river)*(1-estuaryShelter)*(1-town)*
      (this.def.seaRadius===null?1:smooth01((h-this.seaRel)*this.amplitude/3));
    out.canyonRock=canyonRock*(1-river)*(1-estuaryShelter)*(1-town)*
      (this.def.seaRadius===null?1:smooth01((h-this.seaRel)*this.amplitude/3));
    out.canyonGround=canyonGround*(1-river)*(1-estuaryShelter)*(1-town)*
      (this.def.seaRadius===null?1:smooth01((h-this.seaRel)*this.amplitude/3));
    out.estuaryShelter=estuaryShelter*(1-town);
    out.glacierIceM=glacierIceM;
    out.glaciation=glaciation;
    out.volcanism=volcanism;
    // Generated settlement carving cannot flatten or deepen an active flow.
    out.lavaSurfaceM=lavaSurfaceM;
    out.lavaDepthM=lavaSurfaceM>-1e8?Math.max(0,lavaSurfaceM-h*this.amplitude):0;
    out.road = roadSd;
    out.river = river;
    out.mountain = mask;
    out.town = town;
    out.flowx = fx;
    out.flowy = fy;
    out.flowz = fz;
    out.riverS = riverS;
    // A settled road/pad owns dry ground even when it cuts below the old
    // water table. Keep dry natural-bank provenance for plant/surface queries.
    if(riverWs>-1e8&&riverBankHit&&(!riverWaterClaim(riverBankHit,h,this.amplitude)||town>.25||roadSd<0)) {
      riverWs=-1e9;out.river=0;out.flowx=out.flowy=out.flowz=0;riverSpeed=0;
    }
    out.riverWs = riverWs;
    out.riverSpeed = riverSpeed;
    out.riverBankSediment=out.riverBankRock=out.riparian=0;
    if(riverBankHit&&this.def.archetype!=='ice'&&this.def.archetype!=='volcanic'&&glacierIceM<=0&&lavaSurfaceM<-1e8){
      // Reuse the hit already needed for incision; only the final settled
      // height classifies a bank. Specialist geography retains its pigment
      // and habitat authority, including the dry margins of inland wetlands.
      const sea=this.def.seaRadius===null?1:smooth01((Math.min(h,riverBankHit.ws)-this.seaRel)*this.amplitude/3);
      const authority=sea*(1-estuaryShelter)*(1-wetlandMask)*(1-town)*smooth01(roadSd/3)*(1-Math.max(canyonRock,canyonGround));
      if(authority>0){const bank=sampleRiverBank(h,riverBankHit,this.amplitude,this.riverBankScratch);
        out.riverBankSediment=bank.sediment*authority;out.riverBankRock=bank.rock*authority;out.riparian=bank.riparian*authority;
      }
    }

    const terraceAuthority=(1-town)*smooth01(roadSd/3)*(1-wetlandMask)*(1-estuaryShelter)*(1-Math.max(canyonRock,canyonGround));
    out.riverTerrace=riverTerrace*terraceAuthority;
    out.riverBankRock=Math.max(out.riverBankRock??0,terraceRock*terraceAuthority);

    out.snowRetention=-1;
    if (climate) {
      const cl = this.def.climate;
      // Latitude bands cool toward the poles; altitude lapses; noise breaks
      // the bands so biome borders wander.
      const lat = Math.abs(dy);
      out.temp =
        cl.temp -
        lat * lat * cl.latitude * 1.5 -
        Math.max(0, h) * 0.55 +
        (duneClimateReady?duneTempNoise:this.tempN.fbm(dx * 1.3, dy * 1.3, dz * 1.3, 2) * 0.3);
      out.moist =
        cl.moist +
        (duneClimateReady?duneMoistNoise:this.moistN.fbm(dx * 1.6, dy * 1.6, dz * 1.6, 2) * 0.55) +
        (1 - land) * 0.25 + // coasts and lowlands stay wetter
        Math.max(river, lake, bankMoisture * (1 - town)) * 0.3;
      if(!skipHydro&&!skipRivers&&!skipTowns&&alpineEnabled(this.def)&&(out.temp<-.28||snowPotential(out.temp,h*this.amplitude,cl.snowline*this.amplitude)>0)){
        this.alpineShelter??=new AlpineShelter(this.def.radius,this.def.seed,(x,y,z)=>this.drainageH(x,y,z)*this.amplitude);
        out.snowRetention=this.alpineShelter.sample(dx,dy,dz);
      }
    } else {
      out.temp = 0;
      out.moist = 0;
    }
    return out;
  }

  /**
   * Height displacement (can be negative) above the base radius for a unit
   * direction from the planet center. The fast path — no climate fields.
   */
  height(dx: number, dy: number, dz: number): number {
    return this.sample(dx, dy, dz, this.scratch, false).h * this.amplitude;
  }

  /** Absolute radius of the terrain surface along a unit direction. */
  surfaceRadius(dx: number, dy: number, dz: number): number {
    return this.def.radius + this.height(dx, dy, dz);
  }

  /**
   * Outward unit surface normal along a unit direction, via central
   * differences of four tangent-plane samples. `eps` is the sample arc in
   * meters — keep it near the feature size that matters (sub-meter for a
   * walking character). Same deterministic function as the meshes, so
   * slopes match what you see.
   */
  surfaceNormal(dx: number, dy: number, dz: number, out: number[], eps = 0.5): void {
    // Tangent basis: t1 = normalize(axis × d), t2 = d × t1.
    const ax = Math.abs(dx) < 0.9 ? 1 : 0;
    const ay = 1 - ax;
    let t1x = ay * dz, t1y = -ax * dz, t1z = ax * dy - ay * dx;
    const l1 = pmath.hypot(t1x, t1y, t1z) || 1;
    t1x /= l1; t1y /= l1; t1z /= l1;
    const t2x = dy * t1z - dz * t1y;
    const t2y = dz * t1x - dx * t1z;
    const t2z = dx * t1y - dy * t1x;

    const e = eps / this.def.radius;
    const P = (ox: number, oy: number, oz: number): [number, number, number] => {
      let x = dx + ox * e, y = dy + oy * e, z = dz + oz * e;
      const l = pmath.hypot(x, y, z) || 1;
      x /= l; y /= l; z /= l;
      const r = this.surfaceRadius(x, y, z);
      return [x * r, y * r, z * r];
    };
    const pu = P(t1x, t1y, t1z), mu = P(-t1x, -t1y, -t1z);
    const pv = P(t2x, t2y, t2z), mv = P(-t2x, -t2y, -t2z);
    const ux = pu[0] - mu[0], uy = pu[1] - mu[1], uz = pu[2] - mu[2];
    const vx = pv[0] - mv[0], vy = pv[1] - mv[1], vz = pv[2] - mv[2];
    let nx = uy * vz - uz * vy;
    let ny = uz * vx - ux * vz;
    let nz = ux * vy - uy * vx;
    const nl = pmath.hypot(nx, ny, nz) || 1;
    nx /= nl; ny /= nl; nz /= nl;
    // t1 × t2 ≈ d, so the cross is already outward — flip if degenerate.
    if (nx * dx + ny * dy + nz * dz < 0) {
      nx = -nx; ny = -ny; nz = -nz;
    }
    out[0] = nx; out[1] = ny; out[2] = nz;
  }

  /**
   * Downhill flow direction (unit tangent) at a unit direction — steepest
   * descent of the surface height in the local tangent plane. Local and
   * storage-free (no accumulation): reusable for orienting shoreline/▒river foam
   * and as a building block for hydrology. `skipHydro` evaluates the terrain
   * BEFORE rivers are carved (used while building the river graph, avoiding a
   * chicken-and-egg dependency). `out` receives the unit tangent vector.
   * Reused for shoreline/river foam orientation.
   */
  flowDir(dx: number, dy: number, dz: number, out: number[], eps = 2, skipHydro = false): void {
    const ax = Math.abs(dx) < 0.9 ? 1 : 0;
    const ay = 1 - ax;
    let t1x = ay * dz, t1y = -ax * dz, t1z = ax * dy - ay * dx;
    const l1 = pmath.hypot(t1x, t1y, t1z) || 1;
    t1x /= l1; t1y /= l1; t1z /= l1;
    const t2x = dy * t1z - dz * t1y, t2y = dz * t1x - dx * t1z, t2z = dx * t1y - dy * t1x;
    const e = eps / this.def.radius;
    const H = (ox: number, oy: number, oz: number): number => {
      let x = dx + ox * e, y = dy + oy * e, z = dz + oz * e;
      const l = pmath.hypot(x, y, z) || 1;
      return this.sample(x / l, y / l, z / l, this.scratch2, false, skipHydro).h;
    };
    const hu = H(t1x, t1y, t1z) - H(-t1x, -t1y, -t1z);
    const hv = H(t2x, t2y, t2z) - H(-t2x, -t2y, -t2z);
    // Downhill = −gradient, projected into the tangent plane.
    let gx = -(hu * t1x + hv * t2x), gy = -(hu * t1y + hv * t2y), gz = -(hu * t1z + hv * t2z);
    const rad = gx * dx + gy * dy + gz * dz;
    gx -= rad * dx; gy -= rad * dy; gz -= rad * dz;
    const gl = pmath.hypot(gx, gy, gz);
    if (gl < 1e-12) {
      out[0] = t1x; out[1] = t1y; out[2] = t1z; // flat — pick an arbitrary tangent
      return;
    }
    out[0] = gx / gl; out[1] = gy / gl; out[2] = gz / gl;
  }
}
